import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Table, Badge, Modal, Form, Alert, Spinner, Collapse, InputGroup, Pagination } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaEye, FaSave, FaTimes, FaUpload, FaMagic, FaExternalLinkAlt, FaFileAlt, FaRobot, FaPalette, FaBoxOpen, FaCalculator, FaWhatsapp } from 'react-icons/fa';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import AppNavbar from './AppNavbar';
import WhatsappAdminPanel from './WhatsappAdminPanel';
import './Landing.css';
import { useLanguage } from '../contexts/LanguageContext';
import { t, tf } from '../utils/i18n';
import { error as logError } from '../utils/logger';
import '../styles/App.css';

const API_BASE = process.env.REACT_APP_API_URL || '';
const BLOG_CATEGORY_OPTIONS = ['business', 'tech', 'news'];

const featuredImageUrl = (url) =>
  !url ? '' : (url.startsWith('http') ? url : API_BASE + url);

const QUILL_MODULES = (imageHandler) => ({
  toolbar: {
    container: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link', 'image'],
      ['blockquote', 'code-block'],
      [{ color: [] }, { background: [] }],
      ['clean']
    ],
    handlers: imageHandler ? { image: imageHandler } : {}
  }
});

const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike', 'list', 'bullet', 'link', 'image',
  'blockquote', 'code-block', 'color', 'background'
];

const THEME_TOKEN_PRESETS = {
  aurora: { primary: '#667eea', surface: '#ffffff', text: '#1f2937' },
  sunset: { primary: '#f97316', surface: '#ffffff', text: '#1f2937' },
  emerald: { primary: '#059669', surface: '#ffffff', text: '#1f2937' }
};

import {
  DEFAULT_LANDING_SECTIONS,
  LANDING_SECTION_KEYS,
  mergeLandingSections
} from '../utils/landingSections';
import {
  PRODUCT_ICON_COLOR_OPTIONS,
  PRODUCT_ICON_OPTIONS
} from '../utils/productIcons';
import { formatMoneyAmount, getProductSalePricing } from '../utils/productPricing';
import {
  TESTIMONIAL_COUNTRY_CODES,
  countryCodeToFlagEmoji
} from '../utils/countryFlags';
import ProductIcon from './ProductIcon';

const EMPTY_TESTIMONIAL_FORM = {
  id: null,
  quote_es: '',
  quote_en: '',
  person_name: '',
  role_line_es: '',
  role_line_en: '',
  country_code: 'PY',
  avatar_initial: '',
  sort_order: 0,
  active: true
};

function testimonialCountryLabelKey(code) {
  const normalized = String(code || '').trim().toUpperCase();
  if (normalized.length !== 2) return 'blogAdmin.countryPy';
  return `blogAdmin.country${normalized.charAt(0)}${normalized.charAt(1).toLowerCase()}`;
}

function hexToRgb(hex) {
  const normalized = String(hex || '').replace('#', '').trim();
  if (normalized.length !== 6) return null;
  const value = Number.parseInt(normalized, 16);
  if (Number.isNaN(value)) return null;
  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255
  };
}

function relativeLuminance(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  const toLinear = (channel) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return (0.2126 * toLinear(rgb.r)) + (0.7152 * toLinear(rgb.g)) + (0.0722 * toLinear(rgb.b));
}

function getContrastRatio(foregroundHex, backgroundHex) {
  const l1 = relativeLuminance(foregroundHex);
  const l2 = relativeLuminance(backgroundHex);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

function estimateQuote(form) {
  const websiteBase = { landing: 1200, corporate: 2500, ecommerce: 3800 };
  const complexityMult = { low: 1, medium: 1.35, high: 1.75 };
  const timelineMult = { standard: 1, fast: 1.2, urgent: 1.4 };
  const scopeBase = form.projectType === 'software'
    ? 4500 + (Number(form.modules) * 650)
    : (websiteBase[form.websiteType] || 1800);
  const integrationsCost = Number(form.integrations) * 220;
  const automationCost = form.automation ? 700 : 0;
  const seoCost = form.seo ? 320 : 0;
  const uxCost = form.uxResearch ? 420 : 0;
  const supportCost = form.supportMonths > 0 ? (Number(form.supportMonths) * 120) : 0;
  const subtotal = (scopeBase + integrationsCost + automationCost + seoCost + uxCost + supportCost)
    * (complexityMult[form.complexity] || 1)
    * (timelineMult[form.timeline] || 1);
  const low = Math.round(subtotal * 0.9);
  const high = Math.round(subtotal * 1.15);
  return {
    low,
    high,
    score: Math.round((Number(form.modules) * 8) + (Number(form.integrations) * 6) + (form.complexity === 'high' ? 25 : form.complexity === 'medium' ? 15 : 8)),
    assumptions: [
      'quoteAssumptionOne',
      'quoteAssumptionTwo',
      'quoteAssumptionThree'
    ]
  };
}

const BlogAdmin = () => {
  const navigate = useNavigate();
  const { language, loading: languageLoading } = useLanguage();
  // Asegurar que siempre tengamos un idioma válido
  const currentLanguage = language || 'es';
  const [posts, setPosts] = useState([]);
  const [totalPosts, setTotalPosts] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // '' = all, 'true' = published, 'false' = draft
  const [categoryFilter, setCategoryFilter] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  const applySearch = () => {
    setSearchQuery(searchInput);
    setPage(1);
  };
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(5);
  const [user, setUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    category: 'business',
    content: '',
    excerpt: '',
    featured_image: '',
    published: false,
    published_at: '',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    language: 'es',
    related_title: '',
    related_year: ''
  });
  const [saving, setSaving] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingContactSettings, setSavingContactSettings] = useState(false);
  const [uploadingFeatured, setUploadingFeatured] = useState(false);
  const [uploadingEditorImage, setUploadingEditorImage] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateTopic, setGenerateTopic] = useState('');
  const [generateLanguage, setGenerateLanguage] = useState('es');
  const [generating, setGenerating] = useState(false);
  const [lastUsage, setLastUsage] = useState(null);
  const [lastPromptSent, setLastPromptSent] = useState(null);
  const [showPromptSent, setShowPromptSent] = useState(false);
  const [groqApiKeyDraft, setGroqApiKeyDraft] = useState('');
  const [groqKeyConfigured, setGroqKeyConfigured] = useState(false);
  const [groqMessage, setGroqMessage] = useState(null);
  const [groqQuota, setGroqQuota] = useState(null);
  const [groqQuotaLoading, setGroqQuotaLoading] = useState(false);
  const [aiProvider, setAiProvider] = useState('groq');
  const [aiModel, setAiModel] = useState('');
  const [aiApiKeyDraft, setAiApiKeyDraft] = useState('');
  const [aiRegion, setAiRegion] = useState('ap-southeast-1');
  const [availableProviders, setAvailableProviders] = useState([]);
  const [providerMatrix, setProviderMatrix] = useState([]);
  const [aiFallbackOrder, setAiFallbackOrder] = useState([]);
  const [aiTimeoutMs, setAiTimeoutMs] = useState('30000');
  const [aiTemperature, setAiTemperature] = useState('0.5');
  const [providerHealth, setProviderHealth] = useState([]);
  const [providerHealthLoading, setProviderHealthLoading] = useState(false);
  const [uiTheme, setUiTheme] = useState('aurora');
  const [availableUiThemes, setAvailableUiThemes] = useState([]);
  const [uiSectionStyles, setUiSectionStyles] = useState({
    hero: 'default',
    offers: 'default',
    cta: 'default'
  });
  const [landingSections, setLandingSections] = useState(DEFAULT_LANDING_SECTIONS);
  const [languageSelectorEnabled, setLanguageSelectorEnabled] = useState(false);
  const [activePanel, setActivePanel] = useState('posts');
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogMessage, setCatalogMessage] = useState(null);
  const [siteSettingsMessage, setSiteSettingsMessage] = useState(null);
  const [contactSettingsMessage, setContactSettingsMessage] = useState(null);
  const [contactSettings, setContactSettings] = useState({
    whatsappParaguay: '',
    whatsappBolivia: '',
    email: '',
    supportNoteEs: '',
    supportNoteEn: ''
  });
  const [postsMessage, setPostsMessage] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ id: null, slug: '', name: '', description: '' });
  const [productForm, setProductForm] = useState({
    id: null,
    category_id: '',
    name: '',
    slug: '',
    description: '',
    price: '',
    compare_at_price: '',
    currency: 'USD',
    active: true,
    direct_checkout: false,
    primary_image_url: '',
    og_image_url: '',
    gallery_images: [],
    icon_key: 'FaBoxOpen',
    icon_color: 'cyan'
  });
  const [uploadingProductImage, setUploadingProductImage] = useState(false);
  const [productActiveSavingId, setProductActiveSavingId] = useState(null);
  const [productDirectCheckoutSavingId, setProductDirectCheckoutSavingId] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialsLoading, setTestimonialsLoading] = useState(false);
  const [testimonialsMessage, setTestimonialsMessage] = useState(null);
  const [testimonialForm, setTestimonialForm] = useState(EMPTY_TESTIMONIAL_FORM);
  const [testimonialActiveSavingId, setTestimonialActiveSavingId] = useState(null);
  const [quoteStep, setQuoteStep] = useState(1);
  const [quoteForm, setQuoteForm] = useState({
    projectType: 'website',
    websiteType: 'corporate',
    modules: 3,
    integrations: 2,
    complexity: 'medium',
    timeline: 'standard',
    automation: true,
    seo: true,
    uxResearch: false,
    supportMonths: 3,
    currency: 'USD'
  });
  const [quoteResult, setQuoteResult] = useState(null);
  const [quoteSaving, setQuoteSaving] = useState(false);
  const [quoteMessage, setQuoteMessage] = useState(null);
  const [salesQuotes, setSalesQuotes] = useState([]);
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesOpsLoading, setSalesOpsLoading] = useState(false);
  const [staffUsers, setStaffUsers] = useState([]);
  const [salesFilters, setSalesFilters] = useState({
    stage: '',
    projectType: '',
    assignedTo: '',
    search: ''
  });
  const quillRef = useRef(null);
  const featuredFileInputRef = useRef(null);

  const contrastAudit = useMemo(() => {
    const tokens = THEME_TOKEN_PRESETS[uiTheme] || THEME_TOKEN_PRESETS.aurora;
    const checks = [
      {
        key: 'primaryOnSurface',
        ratio: getContrastRatio(tokens.primary, tokens.surface),
        min: 4.5
      },
      {
        key: 'textOnSurface',
        ratio: getContrastRatio(tokens.text, tokens.surface),
        min: 4.5
      }
    ].map((item) => ({
      ...item,
      pass: item.ratio >= item.min
    }));
    return {
      checks,
      allPass: checks.every((check) => check.pass)
    };
  }, [uiTheme]);

  const reorderFallbackProvider = (providerId, direction) => {
    setAiFallbackOrder((prev) => {
      const list = Array.isArray(prev) && prev.length
        ? [...prev]
        : (availableProviders || []).map((p) => p.id);
      const index = list.indexOf(providerId);
      if (index < 0) return list;
      const target = direction === 'up' ? index - 1 : index + 1;
      if (target < 0 || target >= list.length) return list;
      [list[index], list[target]] = [list[target], list[index]];
      return list;
    });
  };

  const handleEditorImage = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      setUploadingEditorImage(true);
      try {
        const fd = new FormData();
        fd.append('image', file);
        if (formData.title) fd.append('title', formData.title);
        if (editingPost?.slug) fd.append('slug', editingPost.slug);
        const res = await fetch(`${API_BASE}/api/blog/upload`, {
          method: 'POST',
          credentials: 'include',
          body: fd
        });
        if (!res.ok) throw new Error('Upload failed');
        const data = await res.json();
        const editor = quillRef.current?.getEditor?.();
        if (editor && data.url) {
          const range = editor.getSelection(true) || { index: editor.getLength() };
          editor.insertEmbed(range.index, 'image', data.url);
        }
      } catch (err) {
        logError(err);
      } finally {
        setUploadingEditorImage(false);
      }
    };
    input.click();
  }, [formData.title, editingPost?.slug]);

  const quillModules = useMemo(() => QUILL_MODULES(handleEditorImage), [handleEditorImage]);

  const handleFeaturedImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFeatured(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      if (formData.title) fd.append('title', formData.title);
      if (editingPost?.slug) fd.append('slug', editingPost.slug);
      const res = await fetch(`${API_BASE}/api/blog/upload`, {
        method: 'POST',
        credentials: 'include',
        body: fd
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      if (data.url) setFormData((prev) => ({ ...prev, featured_image: data.url }));
    } catch (err) {
      logError(err);
    } finally {
      setUploadingFeatured(false);
      e.target.value = '';
    }
  };

  useEffect(() => {
    checkAuthAndLoad();
  }, []);

  useEffect(() => {
    if (user && !checkingAuth) loadPosts();
  }, [page, statusFilter, categoryFilter, searchQuery, user, checkingAuth]);

  useEffect(() => {
    if (!accessDenied) return;
    const intervalId = setInterval(() => {
      setRedirectCountdown((s) => {
        if (s <= 1) {
          clearInterval(intervalId);
          navigate('/user', { replace: true });
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(intervalId);
  }, [accessDenied, navigate]);

  const checkAuthAndLoad = async () => {
    try {
      setCheckingAuth(true);
      // Verificar autenticación primero
      const authResponse = await fetch(`${API_BASE}/auth/me`, { credentials: 'include' });
      if (!authResponse.ok || authResponse.status === 401) {
        navigate('/user');
        return;
      }
      
      const userData = await authResponse.json();
      if (!['admin', 'manager', 'sales'].includes(userData.role)) {
        setAccessDenied(true);
        setCheckingAuth(false);
        setLoading(false);
        return;
      }
      setUser(userData);
      setCheckingAuth(false);
    } catch (err) {
      logError('Error verificando autenticación:', err);
      navigate('/user');
    }
  };

  const loadPosts = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String((page - 1) * PAGE_SIZE));
      if (statusFilter !== '') params.set('published', statusFilter);
      if (categoryFilter) params.set('category', categoryFilter);
      if (searchQuery.trim()) params.set('search', searchQuery.trim());
      const response = await fetch(`${API_BASE}/api/blog/admin/posts?${params}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        if (response.status === 403) {
          setAccessDenied(true);
        } else {
          setError(t('common.blog.errorLoading', currentLanguage));
        }
        setLoading(false);
        return;
      }
      
      const data = await response.json();
      setPosts(data.posts || []);
      setTotalPosts(data.total != null ? data.total : (data.posts || []).length);
    } catch (err) {
      logError('Error cargando posts:', err);
      setError(t('common.blog.errorLoading', currentLanguage));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!checkingAuth && user) loadPosts();
  }, [page, statusFilter, categoryFilter, searchQuery, user, checkingAuth]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    fetch(`${API_BASE}/api/config`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((cfg) => {
        if (cancelled || !cfg) return;
        setGroqKeyConfigured(!!cfg.aiApiKey);
        setGroqApiKeyDraft('');
        setAiProvider(cfg.aiProvider || 'groq');
        setAiModel(cfg.aiModel || '');
        setAiRegion(cfg.aiRegion || 'ap-southeast-1');
        setAvailableProviders(Array.isArray(cfg.providers) ? cfg.providers : []);
        setProviderMatrix(Array.isArray(cfg.providerMatrix) ? cfg.providerMatrix : []);
        setAiFallbackOrder(Array.isArray(cfg.aiFallbackOrder) ? cfg.aiFallbackOrder : []);
        setAiTimeoutMs(String(cfg.aiTimeoutMs || 30000));
        setAiTemperature(String(cfg.aiTemperature ?? 0.5));
        setUiTheme(cfg.uiTheme || 'aurora');
        setAvailableUiThemes(Array.isArray(cfg.uiThemes) ? cfg.uiThemes : []);
        setUiSectionStyles({
          hero: cfg.uiSectionStyles?.hero || 'default',
          offers: cfg.uiSectionStyles?.offers || 'default',
          cta: cfg.uiSectionStyles?.cta || 'default'
        });
        setLandingSections(mergeLandingSections(cfg.landingSections));
        setLanguageSelectorEnabled(cfg?.languageSelectorEnabled === true);
        setContactSettings({
          whatsappParaguay: cfg.siteContact?.whatsappParaguay || '',
          whatsappBolivia: cfg.siteContact?.whatsappBolivia || '',
          email: cfg.siteContact?.email || '',
          supportNoteEs: cfg.siteContact?.supportNoteEs || '',
          supportNoteEn: cfg.siteContact?.supportNoteEn || ''
        });
        setAiApiKeyDraft('');
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    if (!user) return;
    loadCatalog();
    loadTestimonials();
  }, [user]);

  useEffect(() => {
    if (!user) return;
    loadSalesQuotes();
  }, [user]);

  useEffect(() => {
    if (!user) return;
    loadStaffUsers();
  }, [user]);

  useEffect(() => {
    if (!user) return;
    loadSalesQuotes();
  }, [salesFilters, user]);

  useEffect(() => {
    if (!user || activePanel !== 'ai') return;
    refreshProviderHealth();
  }, [user, activePanel]);

  useEffect(() => {
    if (!uiTheme) return;
    document.documentElement.setAttribute('data-theme-template', uiTheme);
  }, [uiTheme]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme-hero-layout', uiSectionStyles.hero || 'default');
    document.documentElement.setAttribute('data-theme-offers-layout', uiSectionStyles.offers || 'default');
    document.documentElement.setAttribute('data-theme-cta-layout', uiSectionStyles.cta || 'default');
  }, [uiSectionStyles]);

  const saveGroqApiKey = async () => {
    setGroqMessage(null);
    setSiteSettingsMessage(null);
    setContactSettingsMessage(null);
    setSavingSettings(true);
    try {
      const res = await fetch(`${API_BASE}/api/me/settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          groq_api_key: aiProvider === 'groq' ? (aiApiKeyDraft.trim() || null) : undefined,
          ai_provider: aiProvider,
          ai_model: aiModel.trim() || undefined,
          ai_api_key: aiApiKeyDraft.trim() || null,
          ai_region: aiRegion.trim() || 'ap-southeast-1',
          ai_fallback_order: aiFallbackOrder,
          ai_timeout_ms: Number(aiTimeoutMs) || 30000,
          ai_temperature: Number(aiTemperature),
          ui_theme: uiTheme,
          ui_section_styles: uiSectionStyles,
          landing_sections: landingSections,
          language_selector_enabled: languageSelectorEnabled,
          ui_contact: user?.role === 'admin' ? contactSettings : undefined
        })
      });
      if (!res.ok) {
        const errText = t('blogAdmin.groqKeyError', currentLanguage);
        setGroqMessage({ variant: 'danger', text: errText });
        setSiteSettingsMessage({ variant: 'danger', text: errText });
        return;
      }
      const cfgRes = await fetch(`${API_BASE}/api/config`, { credentials: 'include' });
      const cfg = cfgRes.ok ? await cfgRes.json() : null;
      setGroqKeyConfigured(!!cfg?.aiApiKey);
      setAiProvider(cfg?.aiProvider || 'groq');
      setAiModel(cfg?.aiModel || '');
      setAiRegion(cfg?.aiRegion || 'ap-southeast-1');
      setAvailableProviders(Array.isArray(cfg?.providers) ? cfg.providers : []);
      setProviderMatrix(Array.isArray(cfg?.providerMatrix) ? cfg.providerMatrix : []);
      setAiFallbackOrder(Array.isArray(cfg?.aiFallbackOrder) ? cfg.aiFallbackOrder : []);
      setAiTimeoutMs(String(cfg?.aiTimeoutMs || 30000));
      setAiTemperature(String(cfg?.aiTemperature ?? 0.5));
      setUiTheme(cfg?.uiTheme || 'aurora');
      setAvailableUiThemes(Array.isArray(cfg?.uiThemes) ? cfg.uiThemes : []);
      setUiSectionStyles({
        hero: cfg?.uiSectionStyles?.hero || 'default',
        offers: cfg?.uiSectionStyles?.offers || 'default',
        cta: cfg?.uiSectionStyles?.cta || 'default'
      });
      setLandingSections(mergeLandingSections(cfg?.landingSections));
      setLanguageSelectorEnabled(cfg?.languageSelectorEnabled === true);
      setContactSettings({
        whatsappParaguay: cfg?.siteContact?.whatsappParaguay || '',
        whatsappBolivia: cfg?.siteContact?.whatsappBolivia || '',
        email: cfg?.siteContact?.email || '',
        supportNoteEs: cfg?.siteContact?.supportNoteEs || '',
        supportNoteEn: cfg?.siteContact?.supportNoteEn || ''
      });
      setAiApiKeyDraft('');
      setGroqMessage({ variant: 'success', text: t('blogAdmin.groqKeySaved', currentLanguage) });
      setSiteSettingsMessage({ variant: 'success', text: t('blogAdmin.siteSettingsSaved', currentLanguage) });
    } catch (e) {
      const errText = t('blogAdmin.groqKeyError', currentLanguage);
      setGroqMessage({ variant: 'danger', text: errText });
      setSiteSettingsMessage({ variant: 'danger', text: errText });
    } finally {
      setSavingSettings(false);
    }
  };

  const saveContactSettings = async () => {
    setGroqMessage(null);
    setSiteSettingsMessage(null);
    setContactSettingsMessage(null);
    setSavingContactSettings(true);
    try {
      const res = await fetch(`${API_BASE}/api/me/settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ui_contact: contactSettings
        })
      });
      if (!res.ok) {
        setContactSettingsMessage({ variant: 'danger', text: t('blogAdmin.contactSettingsSaveError', currentLanguage) });
        return;
      }
      const cfgRes = await fetch(`${API_BASE}/api/config`, { credentials: 'include' });
      const cfg = cfgRes.ok ? await cfgRes.json() : null;
      setContactSettings({
        whatsappParaguay: cfg?.siteContact?.whatsappParaguay || '',
        whatsappBolivia: cfg?.siteContact?.whatsappBolivia || '',
        email: cfg?.siteContact?.email || '',
        supportNoteEs: cfg?.siteContact?.supportNoteEs || '',
        supportNoteEn: cfg?.siteContact?.supportNoteEn || ''
      });
      setContactSettingsMessage({ variant: 'success', text: t('blogAdmin.contactSettingsSaved', currentLanguage) });
    } catch (e) {
      setContactSettingsMessage({ variant: 'danger', text: t('blogAdmin.contactSettingsSaveError', currentLanguage) });
    } finally {
      setSavingContactSettings(false);
    }
  };

  async function refreshProviderHealth() {
    setProviderHealthLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/ai/providers/health`, { credentials: 'include' });
      const data = res.ok ? await res.json() : null;
      setProviderHealth(Array.isArray(data?.providers) ? data.providers : []);
    } catch (e) {
      setProviderHealth([]);
    } finally {
      setProviderHealthLoading(false);
    }
  }

  const refreshGroqQuota = async () => {
    setGroqQuotaLoading(true);
    setGroqQuota(null);
    try {
      const res = await fetch(`${API_BASE}/api/groq/quota`, { credentials: 'include' });
      const data = res.ok ? await res.json() : null;
      setGroqQuota(data);
    } catch (e) {
      setGroqQuota(null);
    } finally {
      setGroqQuotaLoading(false);
    }
  };

  const loadCatalog = async () => {
    setCatalogLoading(true);
    setCatalogMessage(null);
    try {
      const [categoriesRes, productsRes] = await Promise.all([
        fetch(`${API_BASE}/api/catalog/categories`, { credentials: 'include' }),
        fetch(`${API_BASE}/api/catalog/products`, { credentials: 'include' })
      ]);
      const categoriesData = categoriesRes.ok ? await categoriesRes.json() : { categories: [] };
      const productsData = productsRes.ok ? await productsRes.json() : { products: [] };
      setCategories(Array.isArray(categoriesData.categories) ? categoriesData.categories : []);
      setProducts(Array.isArray(productsData.products) ? productsData.products : []);
    } catch (e) {
      logError('Error loading catalog:', e);
      setCatalogMessage({ variant: 'danger', text: t('blogAdmin.catalogLoadError', currentLanguage) });
    } finally {
      setCatalogLoading(false);
    }
  };

  const loadTestimonials = async () => {
    setTestimonialsLoading(true);
    setTestimonialsMessage(null);
    try {
      const res = await fetch(`${API_BASE}/api/testimonials`, { credentials: 'include' });
      const data = res.ok ? await res.json() : { testimonials: [] };
      setTestimonials(Array.isArray(data.testimonials) ? data.testimonials : []);
    } catch (e) {
      logError('Error loading testimonials:', e);
      setTestimonialsMessage({ variant: 'danger', text: t('blogAdmin.testimonialsLoadError', currentLanguage) });
    } finally {
      setTestimonialsLoading(false);
    }
  };

  const loadSalesQuotes = async () => {
    setSalesLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', '50');
      if (salesFilters.stage) params.set('stage', salesFilters.stage);
      if (salesFilters.projectType) params.set('project_type', salesFilters.projectType);
      if (salesFilters.assignedTo) params.set('assigned_to', salesFilters.assignedTo);
      if (salesFilters.search.trim()) params.set('search', salesFilters.search.trim());
      const res = await fetch(`${API_BASE}/api/sales/quotes?${params.toString()}`, { credentials: 'include' });
      const data = res.ok ? await res.json() : { quotes: [] };
      setSalesQuotes(Array.isArray(data.quotes) ? data.quotes : []);
    } catch (e) {
      logError('Error loading sales quotes:', e);
    } finally {
      setSalesLoading(false);
    }
  };

  const loadStaffUsers = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/staff/users`, { credentials: 'include' });
      const data = res.ok ? await res.json() : { users: [] };
      setStaffUsers(Array.isArray(data.users) ? data.users : []);
    } catch (e) {
      logError('Error loading staff users:', e);
      setStaffUsers([]);
    }
  };

  const saveCurrentQuote = async () => {
    if (!quoteResult) return;
    setQuoteSaving(true);
    setQuoteMessage(null);
    try {
      const res = await fetch(`${API_BASE}/api/sales/quotes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          project_type: quoteForm.projectType,
          website_type: quoteForm.websiteType,
          modules: Number(quoteForm.modules),
          integrations: Number(quoteForm.integrations),
          complexity: quoteForm.complexity,
          timeline: quoteForm.timeline,
          support_months: Number(quoteForm.supportMonths),
          automation: !!quoteForm.automation,
          seo: !!quoteForm.seo,
          ux_research: !!quoteForm.uxResearch,
          currency: quoteForm.currency,
          estimate_low: quoteResult.low,
          estimate_high: quoteResult.high,
          complexity_score: quoteResult.score,
          stage: 'new',
          meta: {
            assumptions: quoteResult.assumptions
          }
        })
      });
      if (!res.ok) {
        setQuoteMessage({ variant: 'danger', text: t('blogAdmin.quoteSaveError', currentLanguage) });
        return;
      }
      setQuoteMessage({ variant: 'success', text: t('blogAdmin.quoteSaved', currentLanguage) });
      await loadSalesQuotes();
    } catch (e) {
      logError('Error saving sales quote:', e);
      setQuoteMessage({ variant: 'danger', text: t('blogAdmin.quoteSaveError', currentLanguage) });
    } finally {
      setQuoteSaving(false);
    }
  };

  const updateQuoteStage = async (quoteId, stage) => {
    try {
      const res = await fetch(`${API_BASE}/api/sales/quotes/${quoteId}/stage`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ stage })
      });
      if (!res.ok) {
        setQuoteMessage({ variant: 'danger', text: t('blogAdmin.quoteStageUpdateError', currentLanguage) });
        return;
      }
      setQuoteMessage({ variant: 'success', text: t('blogAdmin.quoteStageUpdated', currentLanguage) });
      await loadSalesQuotes();
    } catch (e) {
      logError('Error updating quote stage:', e);
      setQuoteMessage({ variant: 'danger', text: t('blogAdmin.quoteStageUpdateError', currentLanguage) });
    }
  };

  const updateQuoteDetails = async (quoteId, payload) => {
    setSalesOpsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/sales/quotes/${quoteId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        setQuoteMessage({ variant: 'danger', text: t('blogAdmin.quoteUpdateError', currentLanguage) });
        return;
      }
      setQuoteMessage({ variant: 'success', text: t('blogAdmin.quoteUpdated', currentLanguage) });
      await loadSalesQuotes();
    } catch (e) {
      logError('Error updating quote details:', e);
      setQuoteMessage({ variant: 'danger', text: t('blogAdmin.quoteUpdateError', currentLanguage) });
    } finally {
      setSalesOpsLoading(false);
    }
  };

  const exportQuotesCsv = async () => {
    setSalesOpsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/sales/quotes/export.csv`, { credentials: 'include' });
      if (!res.ok) {
        setQuoteMessage({ variant: 'danger', text: t('blogAdmin.quoteExportError', currentLanguage) });
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sales-quotes-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setQuoteMessage({ variant: 'success', text: t('blogAdmin.quoteExportCsvOk', currentLanguage) });
    } catch (e) {
      logError('Error exporting CSV:', e);
      setQuoteMessage({ variant: 'danger', text: t('blogAdmin.quoteExportError', currentLanguage) });
    } finally {
      setSalesOpsLoading(false);
    }
  };

  const exportQuotesCrm = async () => {
    setSalesOpsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/sales/quotes/export.crm.json`, { credentials: 'include' });
      if (!res.ok) {
        setQuoteMessage({ variant: 'danger', text: t('blogAdmin.quoteExportError', currentLanguage) });
        return;
      }
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sales-quotes-crm-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setQuoteMessage({ variant: 'success', text: t('blogAdmin.quoteExportCrmOk', currentLanguage) });
    } catch (e) {
      logError('Error exporting CRM:', e);
      setQuoteMessage({ variant: 'danger', text: t('blogAdmin.quoteExportError', currentLanguage) });
    } finally {
      setSalesOpsLoading(false);
    }
  };

  const runQuoteReminders = async () => {
    setSalesOpsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/sales/quotes/reminders/run`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = res.ok ? await res.json() : null;
      if (!res.ok) {
        setQuoteMessage({ variant: 'danger', text: t('blogAdmin.quoteRemindersRunError', currentLanguage) });
        return;
      }
      const count = data?.summary?.generated ?? 0;
      setQuoteMessage({ variant: 'success', text: `${t('blogAdmin.quoteRemindersRunOk', currentLanguage)} (${count})` });
    } catch (e) {
      logError('Error running reminders:', e);
      setQuoteMessage({ variant: 'danger', text: t('blogAdmin.quoteRemindersRunError', currentLanguage) });
    } finally {
      setSalesOpsLoading(false);
    }
  };

  const resetCategoryForm = () => setCategoryForm({ id: null, slug: '', name: '', description: '' });
  const resetProductForm = () => setProductForm({
    id: null,
    category_id: '',
    name: '',
    slug: '',
    description: '',
    price: '',
    compare_at_price: '',
    currency: 'USD',
    active: true,
    direct_checkout: false,
    primary_image_url: '',
    og_image_url: '',
    gallery_images: [],
    icon_key: 'FaBoxOpen',
    icon_color: 'cyan'
  });

  const resetTestimonialForm = () => setTestimonialForm(EMPTY_TESTIMONIAL_FORM);

  const editTestimonial = (item) => {
    setTestimonialForm({
      id: item.id,
      quote_es: item.quote_es || '',
      quote_en: item.quote_en || '',
      person_name: item.person_name || '',
      role_line_es: item.role_line_es || '',
      role_line_en: item.role_line_en || '',
      country_code: String(item.country_code || 'PY').toUpperCase(),
      avatar_initial: item.avatar_initial || '',
      sort_order: item.sort_order ?? 0,
      active: item.active !== false
    });
  };

  const handleTestimonialSubmit = async (e) => {
    e.preventDefault();
    if (!testimonialForm.quote_es?.trim() || !testimonialForm.quote_en?.trim() || !testimonialForm.person_name?.trim()) {
      setTestimonialsMessage({ variant: 'danger', text: t('blogAdmin.testimonialsRequiredFields', currentLanguage) });
      return;
    }
    try {
      const isEdit = !!testimonialForm.id;
      const endpoint = isEdit
        ? `${API_BASE}/api/testimonials/${testimonialForm.id}`
        : `${API_BASE}/api/testimonials`;
      const res = await fetch(endpoint, {
        method: isEdit ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          quote_es: testimonialForm.quote_es.trim(),
          quote_en: testimonialForm.quote_en.trim(),
          person_name: testimonialForm.person_name.trim(),
          role_line_es: testimonialForm.role_line_es.trim(),
          role_line_en: testimonialForm.role_line_en.trim(),
          country_code: testimonialForm.country_code,
          avatar_initial: testimonialForm.avatar_initial.trim(),
          sort_order: Number(testimonialForm.sort_order) || 0,
          active: !!testimonialForm.active
        })
      });
      if (!res.ok) {
        setTestimonialsMessage({ variant: 'danger', text: t('blogAdmin.testimonialsSaveError', currentLanguage) });
        return;
      }
      setTestimonialsMessage({ variant: 'success', text: t('blogAdmin.testimonialSaved', currentLanguage) });
      resetTestimonialForm();
      await loadTestimonials();
    } catch (err) {
      logError('Error saving testimonial:', err);
      setTestimonialsMessage({ variant: 'danger', text: t('blogAdmin.testimonialsSaveError', currentLanguage) });
    }
  };

  const deleteTestimonialItem = async (id) => {
    if (!window.confirm(t('blogAdmin.confirmDeleteTestimonial', currentLanguage))) return;
    try {
      const res = await fetch(`${API_BASE}/api/testimonials/${id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) {
        setTestimonialsMessage({ variant: 'danger', text: t('blogAdmin.testimonialsDeleteError', currentLanguage) });
        return;
      }
      setTestimonialsMessage({ variant: 'success', text: t('blogAdmin.testimonialDeleted', currentLanguage) });
      if (testimonialForm.id === id) resetTestimonialForm();
      await loadTestimonials();
    } catch (err) {
      logError('Error deleting testimonial:', err);
      setTestimonialsMessage({ variant: 'danger', text: t('blogAdmin.testimonialsDeleteError', currentLanguage) });
    }
  };

  const toggleTestimonialActive = async (item, nextActive) => {
    setTestimonialActiveSavingId(item.id);
    try {
      const res = await fetch(`${API_BASE}/api/testimonials/${item.id}/active`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: nextActive })
      });
      if (!res.ok) {
        setTestimonialsMessage({ variant: 'danger', text: t('blogAdmin.testimonialActiveUpdateError', currentLanguage) });
        return;
      }
      await loadTestimonials();
    } catch (err) {
      logError('Error updating testimonial active state:', err);
      setTestimonialsMessage({ variant: 'danger', text: t('blogAdmin.testimonialActiveUpdateError', currentLanguage) });
    } finally {
      setTestimonialActiveSavingId(null);
    }
  };

  const productImageUrl = (url) => {
    if (!url) return '';
    return url.startsWith('http') ? url : `${API_BASE}${url}`;
  };

  const uploadProductImage = async (file, { asPrimary = false } = {}) => {
    const fd = new FormData();
    fd.append('image', file);
    if (productForm.name) fd.append('title', productForm.name);
    if (productForm.slug) fd.append('slug', `product-${productForm.slug}`);
    const res = await fetch(`${API_BASE}/api/blog/upload`, {
      method: 'POST',
      credentials: 'include',
      body: fd
    });
    if (!res.ok) throw new Error('upload_failed');
    const data = await res.json();
    if (!data.url) throw new Error('upload_failed');
    setProductForm((prev) => {
      if (asPrimary) {
        return {
          ...prev,
          primary_image_url: data.url,
          og_image_url: prev.og_image_url || data.url
        };
      }
      const gallery = Array.isArray(prev.gallery_images) ? [...prev.gallery_images] : [];
      if (!gallery.includes(data.url)) gallery.push(data.url);
      return {
        ...prev,
        gallery_images: gallery,
        og_image_url: prev.og_image_url || data.url
      };
    });
  };

  const handleProductPrimaryUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingProductImage(true);
    try {
      await uploadProductImage(file, { asPrimary: true });
    } catch (err) {
      logError(err);
      setCatalogMessage({ variant: 'danger', text: t('blogAdmin.productImageUploadError', currentLanguage) });
    } finally {
      setUploadingProductImage(false);
      e.target.value = '';
    }
  };

  const handleProductGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []).filter(Boolean);
    if (!files.length) return;
    setUploadingProductImage(true);
    try {
      for (const file of files) {
        // Keep the backend upload endpoint unchanged: upload files one by one.
        await uploadProductImage(file, { asPrimary: false });
      }
    } catch (err) {
      logError(err);
      setCatalogMessage({ variant: 'danger', text: t('blogAdmin.productImageUploadError', currentLanguage) });
    } finally {
      setUploadingProductImage(false);
      e.target.value = '';
    }
  };

  const setProductGalleryAsPrimary = (url) => {
    setProductForm((prev) => ({
      ...prev,
      primary_image_url: url,
      gallery_images: (prev.gallery_images || []).filter((u) => u !== url)
    }));
  };

  const setProductOgImage = (url) => {
    setProductForm((prev) => ({ ...prev, og_image_url: url || '' }));
  };

  const removeProductGalleryImage = (url) => {
    setProductForm((prev) => ({
      ...prev,
      gallery_images: (prev.gallery_images || []).filter((u) => u !== url),
      primary_image_url: prev.primary_image_url === url ? '' : prev.primary_image_url,
      og_image_url: prev.og_image_url === url ? '' : prev.og_image_url
    }));
  };

  const removeProductImageEverywhere = (url) => {
    setProductForm((prev) => ({
      ...prev,
      gallery_images: (prev.gallery_images || []).filter((u) => u !== url),
      primary_image_url: prev.primary_image_url === url ? '' : prev.primary_image_url,
      og_image_url: prev.og_image_url === url ? '' : prev.og_image_url
    }));
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    if (!categoryForm.slug.trim() || !categoryForm.name.trim()) {
      setCatalogMessage({ variant: 'danger', text: t('blogAdmin.catalogRequiredFields', currentLanguage) });
      return;
    }
    try {
      const isEdit = !!categoryForm.id;
      const res = await fetch(
        isEdit ? `${API_BASE}/api/catalog/categories/${categoryForm.id}` : `${API_BASE}/api/catalog/categories`,
        {
          method: isEdit ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            slug: categoryForm.slug.trim(),
            name: categoryForm.name.trim(),
            description: categoryForm.description.trim() || null
          })
        }
      );
      if (!res.ok) {
        setCatalogMessage({ variant: 'danger', text: t('blogAdmin.catalogSaveError', currentLanguage) });
        return;
      }
      setCatalogMessage({ variant: 'success', text: t('blogAdmin.categorySaved', currentLanguage) });
      resetCategoryForm();
      await loadCatalog();
    } catch (e) {
      logError('Error saving category:', e);
      setCatalogMessage({ variant: 'danger', text: t('blogAdmin.catalogSaveError', currentLanguage) });
    }
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    if (!productForm.name.trim() || !productForm.slug.trim() || Number.isNaN(Number(productForm.price))) {
      setCatalogMessage({ variant: 'danger', text: t('blogAdmin.catalogRequiredFields', currentLanguage) });
      return;
    }
    try {
      const isEdit = !!productForm.id;
      const endpoint = isEdit ? `${API_BASE}/api/catalog/products/${productForm.id}` : `${API_BASE}/api/catalog/products`;
      const res = await fetch(endpoint, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          category_id: productForm.category_id ? Number(productForm.category_id) : null,
          name: productForm.name.trim(),
          slug: productForm.slug.trim(),
          description: productForm.description.trim() || null,
          price: Number(productForm.price),
          compare_at_price: productForm.compare_at_price === '' || productForm.compare_at_price === null
            ? null
            : Number(productForm.compare_at_price),
          currency: productForm.currency.trim().toUpperCase() || 'USD',
          active: !!productForm.active,
          direct_checkout: !!productForm.direct_checkout,
          primary_image_url: productForm.primary_image_url?.trim() || null,
          og_image_url: productForm.og_image_url?.trim() || null,
          gallery_images: Array.isArray(productForm.gallery_images) ? productForm.gallery_images : [],
          icon_key: productForm.icon_key?.trim() || null,
          icon_color: productForm.icon_color?.trim() || null
        })
      });
      if (!res.ok) {
        setCatalogMessage({ variant: 'danger', text: t('blogAdmin.catalogSaveError', currentLanguage) });
        return;
      }
      setCatalogMessage({ variant: 'success', text: t('blogAdmin.productSaved', currentLanguage) });
      resetProductForm();
      await loadCatalog();
    } catch (e) {
      logError('Error saving product:', e);
      setCatalogMessage({ variant: 'danger', text: t('blogAdmin.catalogSaveError', currentLanguage) });
    }
  };

  const editCategory = (item) => {
    setCategoryForm({
      id: item.id,
      slug: item.slug || '',
      name: item.name || '',
      description: item.description || ''
    });
  };

  const editProduct = (item) => {
    let gallery = [];
    if (Array.isArray(item.gallery_images)) gallery = item.gallery_images;
    else if (typeof item.gallery_images === 'string') {
      try {
        const parsed = JSON.parse(item.gallery_images);
        if (Array.isArray(parsed)) gallery = parsed;
      } catch {
        gallery = [];
      }
    }
    setProductForm({
      id: item.id,
      category_id: item.category_id || '',
      name: item.name || '',
      slug: item.slug || '',
      description: item.description || '',
      price: item.price ?? '',
      compare_at_price: item.compare_at_price ?? '',
      currency: item.currency || 'USD',
      active: !!item.active,
      direct_checkout: !!item.direct_checkout,
      primary_image_url: item.primary_image_url || '',
      og_image_url: item.og_image_url || '',
      gallery_images: gallery,
      icon_key: item.icon_key || 'FaBoxOpen',
      icon_color: item.icon_color || 'cyan'
    });
  };

  const productAllImages = useMemo(() => {
    const list = [];
    const seen = new Set();
    const push = (url) => {
      const value = String(url || '').trim();
      if (!value || seen.has(value)) return;
      seen.add(value);
      list.push({
        url: value,
        isPrimary: value === (productForm.primary_image_url || ''),
        isOg: value === (productForm.og_image_url || ''),
        isGallery: (productForm.gallery_images || []).includes(value)
      });
    };
    push(productForm.primary_image_url);
    push(productForm.og_image_url);
    (productForm.gallery_images || []).forEach(push);
    return list;
  }, [productForm.primary_image_url, productForm.og_image_url, productForm.gallery_images]);

  const deleteProductItem = async (id) => {
    if (!window.confirm(t('blogAdmin.confirmDeleteProduct', currentLanguage))) return;
    try {
      const res = await fetch(`${API_BASE}/api/catalog/products/${id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) {
        setCatalogMessage({ variant: 'danger', text: t('blogAdmin.catalogDeleteError', currentLanguage) });
        return;
      }
      setCatalogMessage({ variant: 'success', text: t('blogAdmin.productDeleted', currentLanguage) });
      await loadCatalog();
    } catch (e) {
      logError('Error deleting product:', e);
      setCatalogMessage({ variant: 'danger', text: t('blogAdmin.catalogDeleteError', currentLanguage) });
    }
  };

  const toggleProductActive = async (item, nextActive) => {
    setProductActiveSavingId(item.id);
    setCatalogMessage(null);
    try {
      const res = await fetch(`${API_BASE}/api/catalog/products/${item.id}/active`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ active: nextActive })
      });
      if (!res.ok) {
        setCatalogMessage({ variant: 'danger', text: t('blogAdmin.productActiveUpdateError', currentLanguage) });
        return;
      }
      const updated = await res.json();
      setProducts((prev) => prev.map((p) => (p.id === item.id ? { ...p, active: !!updated.active } : p)));
      if (productForm.id === item.id) {
        setProductForm((s) => ({ ...s, active: !!updated.active }));
      }
    } catch (e) {
      logError('Error toggling product active state:', e);
      setCatalogMessage({ variant: 'danger', text: t('blogAdmin.productActiveUpdateError', currentLanguage) });
    } finally {
      setProductActiveSavingId(null);
    }
  };

  const toggleProductDirectCheckout = async (item, nextDirectCheckout) => {
    setProductDirectCheckoutSavingId(item.id);
    setCatalogMessage(null);
    try {
      const res = await fetch(`${API_BASE}/api/catalog/products/${item.id}/direct-checkout`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ direct_checkout: nextDirectCheckout })
      });
      if (!res.ok) {
        setCatalogMessage({ variant: 'danger', text: t('blogAdmin.productDirectCheckoutUpdateError', currentLanguage) });
        return;
      }
      const updated = await res.json();
      setProducts((prev) => prev.map((p) => (p.id === item.id ? { ...p, direct_checkout: !!updated.direct_checkout } : p)));
      if (productForm.id === item.id) {
        setProductForm((s) => ({ ...s, direct_checkout: !!updated.direct_checkout }));
      }
    } catch (e) {
      logError('Error toggling product direct checkout:', e);
      setCatalogMessage({ variant: 'danger', text: t('blogAdmin.productDirectCheckoutUpdateError', currentLanguage) });
    } finally {
      setProductDirectCheckoutSavingId(null);
    }
  };

  const handleNewPost = () => {
    setEditingPost(null);
    setFormData({
      title: '',
      category: 'business',
      content: '',
      excerpt: '',
      featured_image: '',
      published: false,
      published_at: '',
      meta_title: '',
      meta_description: '',
      meta_keywords: '',
      language: 'es',
      related_title: '',
      related_year: ''
    });
    setShowModal(true);
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title || '',
      category: post.category || 'business',
      content: post.content || '',
      excerpt: post.excerpt || '',
      featured_image: post.featured_image || '',
      published: post.published || false,
      published_at: post.published_at ? new Date(post.published_at).toISOString().split('T')[0] : '',
      meta_title: post.meta_title || '',
      meta_description: post.meta_description || '',
      meta_keywords: post.meta_keywords || '',
      language: post.language === 'en' ? 'en' : 'es',
      related_title: post.related_title || '',
      related_year: post.related_year || ''
    });
    setShowModal(true);
  };

  // URL pública: EN /news/{news|tech|business}/slug, ES /noticias/{actualidad|tecnologia|negocios}/slug
  const getCategoryPath = (category, lang) => {
    const paths = {
      en: { news: 'news', tech: 'tech', business: 'business', movie: 'business', series: 'tech', music: 'news' },
      es: { news: 'actualidad', tech: 'tecnologia', business: 'negocios', movie: 'negocios', series: 'tecnologia', music: 'actualidad' }
    };
    return paths[lang === 'en' ? 'en' : 'es'][category] || (lang === 'en' ? 'news' : 'actualidad');
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm(t('common.blog.confirmDelete', currentLanguage))) {
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE}/api/blog/admin/posts/${postId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!response.ok) {
        setPostsMessage({ variant: 'danger', text: t('common.blog.errorDeleting', currentLanguage) });
        return;
      }
      
      await loadPosts();
      setPostsMessage({ variant: 'success', text: t('blogAdmin.postDeletedSuccess', currentLanguage) });
    } catch (err) {
      logError('Error eliminando post:', err);
      setPostsMessage({ variant: 'danger', text: t('common.blog.errorDeleting', currentLanguage) });
    }
  };

  const handleGenerateWithAI = async () => {
    const topic = generateTopic.trim();
    if (!topic) {
      setPostsMessage({ variant: 'danger', text: t('blogAdmin.generateTopicRequired', currentLanguage) });
      return;
    }
    setGenerating(true);
    setLastUsage(null);
    try {
      const res = await fetch(`${API_BASE}/api/blog/admin/generate-post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ topic, language: generateLanguage })
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        setPostsMessage({ variant: 'danger', text: payload.error || t('common.blog.generateError', currentLanguage) });
        return;
      }
      const { post: createdPost, usage, prompt_sent } = payload;
      setLastUsage(usage || null);
      setLastPromptSent(prompt_sent || null);
      setShowPromptSent(!!prompt_sent);
      setShowGenerateModal(false);
      setGenerateTopic('');
      await loadPosts();
      setFormData({
        title: createdPost.title || '',
        category: createdPost.category || 'business',
        content: createdPost.content || '',
        excerpt: createdPost.excerpt || '',
        featured_image: createdPost.featured_image || '',
        published: false,
        published_at: '',
        meta_title: createdPost.meta_title || '',
        meta_description: createdPost.meta_description || '',
        meta_keywords: createdPost.meta_keywords || '',
        language: createdPost.language === 'en' ? 'en' : 'es',
        related_title: createdPost.related_title || '',
        related_year: createdPost.related_year || ''
      });
      setEditingPost(createdPost);
      setShowModal(true);
      setPostsMessage({ variant: 'success', text: t('blogAdmin.postGeneratedSuccess', currentLanguage) });
    } catch (err) {
      logError(err);
      setPostsMessage({ variant: 'danger', text: t('common.blog.generateError', currentLanguage) });
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      setPostsMessage({ variant: 'danger', text: t('blogAdmin.postRequiredFields', currentLanguage) });
      return;
    }
    try {
      setSaving(true);
      const url = editingPost 
        ? `${API_BASE}/api/blog/admin/posts/${editingPost.id}`
        : `${API_BASE}/api/blog/admin/posts`;
      
      const method = editingPost ? 'PUT' : 'POST';
      
      const payload = {
        ...formData,
        published_at: formData.published_at || null
      };
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        setPostsMessage({ variant: 'danger', text: errorData.error || t('common.blog.errorSaving', currentLanguage) });
        return;
      }
      
      setShowModal(false);
      await loadPosts();
      setPostsMessage({ variant: 'success', text: t('blogAdmin.postSavedSuccess', currentLanguage) });
    } catch (err) {
      logError('Error guardando post:', err);
      setPostsMessage({ variant: 'danger', text: t('common.blog.errorSaving', currentLanguage) });
    } finally {
      setSaving(false);
    }
  };

  const buildTemplateByCategory = useCallback((category) => {
    if (category === 'business') {
      return t('blogAdmin.templateContentCaseStudy', currentLanguage);
    }
    if (category === 'tech') {
      return t('blogAdmin.templateContentStrategy', currentLanguage);
    }
    return t('blogAdmin.templateContentTactical', currentLanguage);
  }, [currentLanguage]);

  const applyCommercialTemplate = (category) => {
    setFormData((prev) => ({ ...prev, content: buildTemplateByCategory(category) }));
    setPostsMessage({ variant: 'success', text: t('blogAdmin.templateAppliedMessage', currentLanguage) });
  };

  const resetUiDefaults = () => {
    setUiTheme('aurora');
    setUiSectionStyles({
      hero: 'default',
      offers: 'default',
      cta: 'default'
    });
    setLandingSections({ ...DEFAULT_LANDING_SECTIONS });
    setSiteSettingsMessage({ variant: 'info', text: t('blogAdmin.siteSettingsDefaultsApplied', currentLanguage) });
  };

  const resetLandingSectionsCompact = () => {
    setLandingSections({ ...DEFAULT_LANDING_SECTIONS });
    setSiteSettingsMessage({ variant: 'info', text: t('blogAdmin.landingSectionsDefaultsApplied', currentLanguage) });
  };

  const nextQuoteStep = () => setQuoteStep((s) => Math.min(3, s + 1));
  const prevQuoteStep = () => setQuoteStep((s) => Math.max(1, s - 1));
  const runQuote = () => {
    const result = estimateQuote(quoteForm);
    setQuoteResult(result);
    setQuoteStep(3);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString(currentLanguage === 'en' ? 'en-US' : 'es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCategoryLabel = (category) => {
    const labels = {
      business: t('blogAdmin.contentTypeBusiness', currentLanguage),
      tech: t('blogAdmin.contentTypeTech', currentLanguage),
      news: t('blogAdmin.contentTypeNews', currentLanguage),
      movie: t('blogAdmin.contentTypeBusiness', currentLanguage),
      series: t('blogAdmin.contentTypeTech', currentLanguage),
      music: t('blogAdmin.contentTypeNews', currentLanguage)
    };
    return labels[category] || category;
  };

  const getCategoryColor = (category) => {
    const colors = {
      business: 'primary',
      tech: 'success',
      news: 'warning',
      movie: 'primary',
      series: 'success',
      music: 'warning'
    };
    return colors[category] || 'secondary';
  };

  const getPostUrl = (post) => {
    if (!post.published || !post.slug) return null;
    const lang = post.language === 'en' ? 'en' : 'es';
    const base = lang === 'en' ? '/news' : '/noticias';
    const categoryPath = getCategoryPath(post.category, lang);
    return `${base}/${categoryPath}/${post.slug}`;
  };

  const getProductUrl = (product) => {
    if (!product?.slug) return null;
    const base = currentLanguage === 'en' ? '/services' : '/servicios';
    const path = `${base}/${encodeURIComponent(product.slug)}`;
    return product.active ? path : `${path}?preview=1`;
  };

  const handleLogout = () => {
    setUser(null);
  };

  if (accessDenied) {
    return (
      <div className="landing landing-theme-dark app-admin-shell min-vh-100">
        <AppNavbar onLogout={handleLogout} activeSection="dashboard" />
        <Container className="site-main-with-header py-5">
          <div className="text-center py-5 px-3">
            <div className="display-1 mb-3" role="img" aria-hidden="true" style={{ lineHeight: 1 }}>
              😅
            </div>
            <p className="text-secondary text-uppercase small fw-bold mb-2">
              {t('common.blog.accessDeniedOops', currentLanguage)}
            </p>
            <h1 className="h2 fw-bold mb-3 text-white">{t('common.blog.accessDeniedTitle', currentLanguage)}</h1>
            <p className="lead text-secondary mb-4 mx-auto" style={{ maxWidth: '420px' }}>
              {t('common.blog.accessDeniedMessage', currentLanguage)}
            </p>
            <p className="small text-secondary">
              {tf('common.blog.accessDeniedRedirect', { seconds: String(redirectCountdown) }, currentLanguage)}
            </p>
            <Button variant="outline-light" className="mt-3 border border-info text-info" onClick={() => navigate('/user', { replace: true })}>
              {t('common.blog.accessDeniedGoToAccount', currentLanguage)}
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  if (checkingAuth || loading || languageLoading) {
    return (
      <div className="landing landing-theme-dark app-admin-shell min-vh-100">
        <AppNavbar onLogout={handleLogout} activeSection="dashboard" />
        <div className="site-main-with-header py-5">
          <Container>
            <div className="text-center">
              <Spinner animation="border" variant="light" />
              <p className="mt-3 text-secondary">{checkingAuth ? t('common.checkingAuth', currentLanguage) : t('common.blog.loading', currentLanguage)}</p>
            </div>
          </Container>
        </div>
      </div>
    );
  }

  return (
    <div className="landing landing-theme-dark app-admin-shell min-vh-100">
      <AppNavbar onLogout={handleLogout} activeSection="dashboard" />
      <Container fluid className="site-main-with-header pb-5 px-3 px-xl-4">
      <Card className="mb-3 border-0">
        <Card.Body className="d-flex flex-column flex-lg-row justify-content-between gap-3">
          <div>
            <h1 className="h4 mb-1">{t('blogAdmin.welcomeTitle', currentLanguage)}</h1>
            <p className="text-muted mb-0">{t('blogAdmin.welcomeDesc', currentLanguage)}</p>
          </div>
          <div className="d-flex flex-wrap gap-2 align-items-start align-items-lg-center">
            <Badge bg={activePanel === 'posts' ? 'primary' : 'secondary'}>{t('blogAdmin.quickStatusContent', currentLanguage)}</Badge>
            <Badge bg={activePanel === 'ai' ? 'primary' : 'secondary'}>{t('blogAdmin.quickStatusAi', currentLanguage)}</Badge>
            <Badge bg={activePanel === 'settings' ? 'primary' : 'secondary'}>{t('blogAdmin.quickStatusSite', currentLanguage)}</Badge>
            <Badge bg={activePanel === 'catalog' ? 'primary' : 'secondary'}>{t('blogAdmin.quickStatusCatalog', currentLanguage)}</Badge>
            <Badge bg={activePanel === 'sales' ? 'primary' : 'secondary'}>{t('blogAdmin.quickStatusSales', currentLanguage)}</Badge>
            <Badge bg={activePanel === 'whatsapp' ? 'primary' : 'secondary'}>{t('blogAdmin.quickStatusWhatsapp', currentLanguage)}</Badge>
          </div>
        </Card.Body>
      </Card>
      <Row className="g-3 mb-4">
        <Col md={6} lg={4} xl={3}>
          <Card className={`h-100 ${activePanel === 'posts' ? 'border-primary shadow-sm' : ''}`} role="button" onClick={() => setActivePanel('posts')}>
            <Card.Body>
              <div className="d-flex align-items-center gap-2 mb-2">
                <FaFileAlt className="text-primary" />
                <Card.Title className="h6 mb-0">{t('blogAdmin.cardPostsTitle', currentLanguage)}</Card.Title>
              </div>
              <Card.Text className="small text-muted mb-0">{t('blogAdmin.cardPostsDesc', currentLanguage)}</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} lg={4} xl={3}>
          <Card className={`h-100 ${activePanel === 'ai' ? 'border-primary shadow-sm' : ''}`} role="button" onClick={() => setActivePanel('ai')}>
            <Card.Body>
              <div className="d-flex align-items-center gap-2 mb-2">
                <FaRobot className="text-primary" />
                <Card.Title className="h6 mb-0">{t('blogAdmin.cardAiTitle', currentLanguage)}</Card.Title>
              </div>
              <Card.Text className="small text-muted mb-0">{t('blogAdmin.cardAiDesc', currentLanguage)}</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} lg={4} xl={3}>
          <Card className={`h-100 ${activePanel === 'settings' ? 'border-primary shadow-sm' : ''}`} role="button" onClick={() => setActivePanel('settings')}>
            <Card.Body>
              <div className="d-flex align-items-center gap-2 mb-2">
                <FaPalette className="text-primary" />
                <Card.Title className="h6 mb-0">{t('blogAdmin.cardSettingsTitle', currentLanguage)}</Card.Title>
              </div>
              <Card.Text className="small text-muted mb-0">{t('blogAdmin.cardSettingsDesc', currentLanguage)}</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} lg={4} xl={3}>
          <Card className={`h-100 ${activePanel === 'catalog' ? 'border-primary shadow-sm' : ''}`} role="button" onClick={() => setActivePanel('catalog')}>
            <Card.Body>
              <div className="d-flex align-items-center gap-2 mb-2">
                <FaBoxOpen className="text-primary" />
                <Card.Title className="h6 mb-0">{t('blogAdmin.cardCatalogTitle', currentLanguage)}</Card.Title>
              </div>
              <Card.Text className="small text-muted mb-0">{t('blogAdmin.cardCatalogDesc', currentLanguage)}</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} lg={4} xl={3}>
          <Card className={`h-100 ${activePanel === 'sales' ? 'border-primary shadow-sm' : ''}`} role="button" onClick={() => setActivePanel('sales')}>
            <Card.Body>
              <div className="d-flex align-items-center gap-2 mb-2">
                <FaCalculator className="text-primary" />
                <Card.Title className="h6 mb-0">{t('blogAdmin.cardSalesTitle', currentLanguage)}</Card.Title>
              </div>
              <Card.Text className="small text-muted mb-0">{t('blogAdmin.cardSalesDesc', currentLanguage)}</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} lg={4} xl={3}>
          <Card className={`h-100 ${activePanel === 'whatsapp' ? 'border-primary shadow-sm' : ''}`} role="button" onClick={() => setActivePanel('whatsapp')}>
            <Card.Body>
              <div className="d-flex align-items-center gap-2 mb-2">
                <FaWhatsapp className="text-success" />
                <Card.Title className="h6 mb-0">{t('blogAdmin.cardWhatsappTitle', currentLanguage)}</Card.Title>
              </div>
              <Card.Text className="small text-muted mb-0">{t('blogAdmin.cardWhatsappDesc', currentLanguage)}</Card.Text>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      {activePanel === 'whatsapp' && (
        <WhatsappAdminPanel isAdmin={user?.role === 'admin'} />
      )}
      {activePanel === 'ai' && (
      <Card className="mb-4 border-secondary">
        <Card.Body>
          <h5 className="mb-2">{t('blogAdmin.aiSectionTitle', currentLanguage)}</h5>
          <p className="text-muted small mb-3">{t('blogAdmin.aiSectionHelp', currentLanguage)}</p>
          {groqMessage && (
            <Alert variant={groqMessage.variant} dismissible onClose={() => setGroqMessage(null)} className="py-2">
              {groqMessage.text}
            </Alert>
          )}
          <Row className="g-2 mb-2">
            <Col md={4}>
              <Form.Label>{t('blogAdmin.aiProviderLabel', currentLanguage)}</Form.Label>
              <Form.Select value={aiProvider} onChange={(e) => setAiProvider(e.target.value)}>
                {(availableProviders.length ? availableProviders : [{ id: 'groq', label: 'Groq' }]).map((p) => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label>{t('blogAdmin.aiModelLabel', currentLanguage)}</Form.Label>
              <Form.Control value={aiModel} onChange={(e) => setAiModel(e.target.value)} placeholder="llama-3.3-70b-versatile" />
            </Col>
            <Col md={4}>
              <Form.Label>{t('blogAdmin.aiRegionLabel', currentLanguage)}</Form.Label>
              <Form.Control value={aiRegion} onChange={(e) => setAiRegion(e.target.value)} placeholder="ap-southeast-1" />
            </Col>
            <Col md={4}>
              <Form.Label>{t('blogAdmin.aiTimeoutLabel', currentLanguage)}</Form.Label>
              <Form.Control
                type="number"
                min="1000"
                step="500"
                value={aiTimeoutMs}
                onChange={(e) => setAiTimeoutMs(e.target.value)}
              />
            </Col>
            <Col md={4}>
              <Form.Label>{t('blogAdmin.aiTemperatureLabel', currentLanguage)}</Form.Label>
              <Form.Control
                type="number"
                min="0"
                max="1"
                step="0.1"
                value={aiTemperature}
                onChange={(e) => setAiTemperature(e.target.value)}
              />
            </Col>
          </Row>
          <div className="mb-2">
            <Form.Label>{t('blogAdmin.aiFallbackOrderLabel', currentLanguage)}</Form.Label>
            <div className="d-flex flex-wrap gap-2">
              {(aiFallbackOrder.length ? aiFallbackOrder : (availableProviders || []).map((p) => p.id)).map((providerId, idx, arr) => {
                const provider = (availableProviders || []).find((p) => p.id === providerId);
                return (
                  <InputGroup key={providerId} size="sm" style={{ maxWidth: 280 }}>
                    <InputGroup.Text>
                      {idx + 1}. {provider?.label || providerId}
                    </InputGroup.Text>
                    <Button
                      variant="outline-secondary"
                      onClick={() => reorderFallbackProvider(providerId, 'up')}
                      disabled={idx === 0}
                    >
                      ↑
                    </Button>
                    <Button
                      variant="outline-secondary"
                      onClick={() => reorderFallbackProvider(providerId, 'down')}
                      disabled={idx === arr.length - 1}
                    >
                      ↓
                    </Button>
                  </InputGroup>
                );
              })}
            </div>
            <Form.Text className="text-muted">{t('blogAdmin.aiFallbackOrderHelp', currentLanguage)}</Form.Text>
          </div>
          <Form.Group className="mb-2">
            <Form.Label>{t('blogAdmin.aiApiKeyLabel', currentLanguage)}</Form.Label>
            <Form.Control
              type="password"
              autoComplete="off"
              placeholder={groqKeyConfigured ? t('settings.groqApiKeyConfigured', currentLanguage) : t('settings.groqApiKeyPlaceholder', currentLanguage)}
              value={aiApiKeyDraft}
              onChange={(e) => setAiApiKeyDraft(e.target.value)}
            />
          </Form.Group>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <Button variant="primary" size="sm" onClick={saveGroqApiKey} disabled={savingSettings}>
              {savingSettings ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-2" />
                  {t('common.blog.saving', currentLanguage)}
                </>
              ) : t('blogAdmin.saveGroqKey', currentLanguage)}
            </Button>
            <Button variant="outline-secondary" size="sm" onClick={refreshGroqQuota} disabled={groqQuotaLoading}>
              {groqQuotaLoading ? t('blogAdmin.groqQuotaLoading', currentLanguage) : t('blogAdmin.checkGroqQuota', currentLanguage)}
            </Button>
            <Button variant="outline-secondary" size="sm" onClick={refreshProviderHealth} disabled={providerHealthLoading}>
              {providerHealthLoading ? t('blogAdmin.providerHealthLoading', currentLanguage) : t('blogAdmin.providerHealthRefresh', currentLanguage)}
            </Button>
            {groqQuota && (
              <span className="small text-muted">
                {(() => {
                  const q = groqQuota;
                  if (q.code === 'GROQ_UNAUTHORIZED') return t('blogAdmin.groqQuota401Hint', currentLanguage);
                  if (q.available) {
                    if (q.rateLimitRemaining != null && q.rateLimitLimit != null) {
                      return tf(
                        'blogAdmin.groqQuotaOkRemaining',
                        { remaining: String(q.rateLimitRemaining), limit: String(q.rateLimitLimit) },
                        currentLanguage
                      );
                    }
                    if (q.rateLimitRemaining != null) {
                      return tf('blogAdmin.groqQuotaOkRemainingPartial', { remaining: String(q.rateLimitRemaining) }, currentLanguage);
                    }
                    return t('blogAdmin.groqQuotaOk', currentLanguage);
                  }
                  if (q.limitReached) return t('blogAdmin.groqQuotaLimitReached', currentLanguage);
                  if (q.paymentRequired) return t('blogAdmin.groqQuotaPaymentRequired', currentLanguage);
                  const msg = q.message || '';
                  if (msg.includes('not configured')) return t('blogAdmin.groqQuotaNotConfigured', currentLanguage);
                  if (msg.includes('Failed to verify')) return t('blogAdmin.groqQuotaVerifyFailed', currentLanguage);
                  return msg || t('blogAdmin.groqQuotaUnavailable', currentLanguage);
                })()}
              </span>
            )}
          </div>
          <div className="mt-3">
            <h6 className="fw-bold mb-2">{t('blogAdmin.providerMatrixTitle', currentLanguage)}</h6>
            <Table responsive size="sm" bordered hover className="mb-0">
              <thead>
                <tr>
                  <th>{t('blogAdmin.providerMatrixProvider', currentLanguage)}</th>
                  <th>{t('blogAdmin.providerMatrixModel', currentLanguage)}</th>
                  <th>{t('blogAdmin.providerMatrixTimeout', currentLanguage)}</th>
                  <th>{t('blogAdmin.providerMatrixStatus', currentLanguage)}</th>
                </tr>
              </thead>
              <tbody>
                {(providerMatrix || []).map((row) => {
                  const health = (providerHealth || []).find((p) => p.providerId === row.id);
                  const available = health ? !!health.available : !!row.enabled;
                  return (
                    <tr key={row.id}>
                      <td>
                        <span className="fw-semibold">{row.label || row.id}</span>
                        {row.isPrimary && <Badge bg="primary" className="ms-2">{t('blogAdmin.providerPrimaryBadge', currentLanguage)}</Badge>}
                      </td>
                      <td>{row.model || '—'}</td>
                      <td>{row.timeoutMs || aiTimeoutMs}</td>
                      <td>
                        <Badge bg={available ? 'success' : 'secondary'}>
                          {available ? t('blogAdmin.providerStatusAvailable', currentLanguage) : t('blogAdmin.providerStatusUnavailable', currentLanguage)}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
      )}
      {activePanel === 'settings' && (
      <Card className="mb-4 border-secondary">
        <Card.Body>
          <h5 className="mb-2">{t('blogAdmin.siteSettingsTitle', currentLanguage)}</h5>
          <p className="text-muted small mb-3">{t('blogAdmin.siteSettingsHelp', currentLanguage)}</p>
          {siteSettingsMessage && (
            <Alert variant={siteSettingsMessage.variant} dismissible onClose={() => setSiteSettingsMessage(null)} className="py-2">
              {siteSettingsMessage.text}
            </Alert>
          )}

          <Card id="admin-homepage-sections" className="mb-4 border-primary blog-admin-surface-card">
            <Card.Body>
              <h6 className="fw-bold mb-2">{t('blogAdmin.landingSectionsTitle', currentLanguage)}</h6>
              <p className="text-muted small mb-3">{t('blogAdmin.landingSectionsHelp', currentLanguage)}</p>
              <Row className="g-2 mb-3">
                {LANDING_SECTION_KEYS.map(({ key, labelKey }) => (
                  <Col key={key} xs={12} sm={6} lg={4}>
                    <Form.Check
                      type="switch"
                      id={`landing-section-${key}`}
                      label={t(labelKey, currentLanguage)}
                      checked={!!landingSections[key]}
                      onChange={(e) => setLandingSections((prev) => ({ ...prev, [key]: e.target.checked }))}
                    />
                  </Col>
                ))}
              </Row>
              <Form.Check
                type="switch"
                id="site-language-selector-enabled"
                className="mb-3"
                label={t('blogAdmin.languageSelectorEnabledLabel', currentLanguage)}
                checked={!!languageSelectorEnabled}
                onChange={(e) => setLanguageSelectorEnabled(e.target.checked)}
              />
              <Form.Text className="text-muted d-block mb-3">
                {t('blogAdmin.languageSelectorEnabledHelp', currentLanguage)}
              </Form.Text>
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <Button variant="primary" size="sm" onClick={saveGroqApiKey} disabled={savingSettings}>
                  {savingSettings ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" className="me-2" />
                      {t('common.blog.saving', currentLanguage)}
                    </>
                  ) : t('blogAdmin.saveLandingSections', currentLanguage)}
                </Button>
                <Button variant="outline-secondary" size="sm" onClick={resetLandingSectionsCompact} disabled={savingSettings}>
                  {t('blogAdmin.landingSectionsResetCompact', currentLanguage)}
                </Button>
              </div>
            </Card.Body>
          </Card>

          <Card className="mb-4 border-secondary blog-admin-surface-card">
            <Card.Body>
              <h6 className="fw-bold mb-2">{t('blogAdmin.testimonialsSectionTitle', currentLanguage)}</h6>
              <p className="text-muted small mb-3">{t('blogAdmin.testimonialsSectionHelp', currentLanguage)}</p>
              {testimonialsMessage && (
                <Alert variant={testimonialsMessage.variant} dismissible onClose={() => setTestimonialsMessage(null)} className="py-2">
                  {testimonialsMessage.text}
                </Alert>
              )}
              <Form onSubmit={handleTestimonialSubmit} className="mb-3">
                <Row className="g-2">
                  <Col md={6}>
                    <Form.Label className="small">{t('blogAdmin.testimonialQuoteEsLabel', currentLanguage)}</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={testimonialForm.quote_es}
                      onChange={(e) => setTestimonialForm((s) => ({ ...s, quote_es: e.target.value }))}
                      required
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label className="small">{t('blogAdmin.testimonialQuoteEnLabel', currentLanguage)}</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={testimonialForm.quote_en}
                      onChange={(e) => setTestimonialForm((s) => ({ ...s, quote_en: e.target.value }))}
                      required
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label className="small">{t('blogAdmin.testimonialNameLabel', currentLanguage)}</Form.Label>
                    <Form.Control
                      value={testimonialForm.person_name}
                      onChange={(e) => setTestimonialForm((s) => ({ ...s, person_name: e.target.value }))}
                      required
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label className="small">{t('blogAdmin.testimonialRoleEsLabel', currentLanguage)}</Form.Label>
                    <Form.Control
                      value={testimonialForm.role_line_es}
                      onChange={(e) => setTestimonialForm((s) => ({ ...s, role_line_es: e.target.value }))}
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label className="small">{t('blogAdmin.testimonialRoleEnLabel', currentLanguage)}</Form.Label>
                    <Form.Control
                      value={testimonialForm.role_line_en}
                      onChange={(e) => setTestimonialForm((s) => ({ ...s, role_line_en: e.target.value }))}
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Label className="small">{t('blogAdmin.testimonialCountryLabel', currentLanguage)}</Form.Label>
                    <Form.Select
                      value={testimonialForm.country_code}
                      onChange={(e) => setTestimonialForm((s) => ({ ...s, country_code: e.target.value }))}
                    >
                      {TESTIMONIAL_COUNTRY_CODES.map((code) => (
                        <option key={code} value={code}>
                          {countryCodeToFlagEmoji(code)} {t(testimonialCountryLabelKey(code), currentLanguage)}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={2}>
                    <Form.Label className="small">{t('blogAdmin.testimonialInitialLabel', currentLanguage)}</Form.Label>
                    <Form.Control
                      maxLength={1}
                      value={testimonialForm.avatar_initial}
                      onChange={(e) => setTestimonialForm((s) => ({ ...s, avatar_initial: e.target.value.slice(0, 1) }))}
                    />
                  </Col>
                  <Col md={2}>
                    <Form.Label className="small">{t('blogAdmin.testimonialSortOrderLabel', currentLanguage)}</Form.Label>
                    <Form.Control
                      type="number"
                      value={testimonialForm.sort_order}
                      onChange={(e) => setTestimonialForm((s) => ({ ...s, sort_order: e.target.value }))}
                    />
                  </Col>
                  <Col md={12} className="d-flex flex-wrap gap-2">
                    <Button size="sm" type="submit">{t('blogAdmin.saveTestimonial', currentLanguage)}</Button>
                    {testimonialForm.id && (
                      <Button size="sm" variant="outline-secondary" type="button" onClick={resetTestimonialForm}>
                        {t('blogAdmin.cancelEdit', currentLanguage)}
                      </Button>
                    )}
                  </Col>
                </Row>
              </Form>
              {testimonialsLoading ? (
                <div className="small text-muted">{t('common.loading', currentLanguage)}</div>
              ) : (
                <Table responsive size="sm" className="mb-0 blog-admin-data-table">
                  <thead>
                    <tr>
                      <th>{t('blogAdmin.testimonialTableName', currentLanguage)}</th>
                      <th>{t('blogAdmin.testimonialTableCountry', currentLanguage)}</th>
                      <th>{t('blogAdmin.testimonialTableOrder', currentLanguage)}</th>
                      <th>{t('blogAdmin.testimonialTableEnabled', currentLanguage)}</th>
                      <th className="blog-admin-data-table-actions">{t('common.blog.actions', currentLanguage)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {testimonials.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-muted small">{t('blogAdmin.noTestimonials', currentLanguage)}</td>
                      </tr>
                    ) : testimonials.map((item) => (
                      <tr key={item.id} className={item.active ? undefined : 'blog-admin-product-row-inactive'}>
                        <td>{item.person_name}</td>
                        <td className="text-nowrap">
                          <span className="me-1" aria-hidden>{countryCodeToFlagEmoji(item.country_code)}</span>
                          {t(testimonialCountryLabelKey(item.country_code), currentLanguage)}
                        </td>
                        <td>{item.sort_order}</td>
                        <td>
                          <Form.Check
                            type="switch"
                            id={`testimonial-active-${item.id}`}
                            label={item.active
                              ? t('blogAdmin.testimonialActiveOn', currentLanguage)
                              : t('blogAdmin.testimonialActiveOff', currentLanguage)}
                            checked={!!item.active}
                            disabled={testimonialActiveSavingId === item.id}
                            onChange={(e) => toggleTestimonialActive(item, e.target.checked)}
                          />
                        </td>
                        <td className="blog-admin-data-table-actions">
                          <div className="d-flex flex-wrap gap-1">
                            <Button size="sm" variant="outline-primary" onClick={() => editTestimonial(item)}>
                              {t('common.edit', currentLanguage)}
                            </Button>
                            <Button size="sm" variant="outline-danger" onClick={() => deleteTestimonialItem(item.id)}>
                              {t('common.delete', currentLanguage)}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>

          <h6 className="fw-bold mb-2">{t('blogAdmin.siteSettingsThemeBlockTitle', currentLanguage)}</h6>
          <p className="text-muted small mb-3">{t('blogAdmin.siteSettingsThemeBlockHelp', currentLanguage)}</p>
          <Row className="g-2 mb-2">
            <Col md={4}>
              <Form.Label>{t('blogAdmin.uiThemeLabel', currentLanguage)}</Form.Label>
              <Form.Select value={uiTheme} onChange={(e) => setUiTheme(e.target.value)}>
                {(availableUiThemes.length
                  ? availableUiThemes
                  : [{ id: 'aurora', label: t('blogAdmin.themeAurora', currentLanguage) }]
                ).map((theme) => (
                  <option key={theme.id} value={theme.id}>
                    {theme.label || theme.id}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">{t('blogAdmin.uiThemeHelp', currentLanguage)}</Form.Text>
            </Col>
            <Col md={4}>
              <Form.Label>{t('blogAdmin.uiHeroLayoutLabel', currentLanguage)}</Form.Label>
              <Form.Select
                value={uiSectionStyles.hero}
                onChange={(e) => setUiSectionStyles((s) => ({ ...s, hero: e.target.value }))}
              >
                <option value="default">{t('blogAdmin.uiLayoutDefault', currentLanguage)}</option>
                <option value="compact">{t('blogAdmin.uiLayoutCompact', currentLanguage)}</option>
                <option value="spotlight">{t('blogAdmin.uiLayoutSpotlight', currentLanguage)}</option>
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label>{t('blogAdmin.uiOffersLayoutLabel', currentLanguage)}</Form.Label>
              <Form.Select
                value={uiSectionStyles.offers}
                onChange={(e) => setUiSectionStyles((s) => ({ ...s, offers: e.target.value }))}
              >
                <option value="default">{t('blogAdmin.uiLayoutDefault', currentLanguage)}</option>
                <option value="cards">{t('blogAdmin.uiLayoutCards', currentLanguage)}</option>
                <option value="minimal">{t('blogAdmin.uiLayoutMinimal', currentLanguage)}</option>
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label>{t('blogAdmin.uiCtaLayoutLabel', currentLanguage)}</Form.Label>
              <Form.Select
                value={uiSectionStyles.cta}
                onChange={(e) => setUiSectionStyles((s) => ({ ...s, cta: e.target.value }))}
              >
                <option value="default">{t('blogAdmin.uiLayoutDefault', currentLanguage)}</option>
                <option value="split">{t('blogAdmin.uiLayoutSplit', currentLanguage)}</option>
                <option value="banner">{t('blogAdmin.uiLayoutBanner', currentLanguage)}</option>
              </Form.Select>
            </Col>
          </Row>
          <div className="d-flex flex-wrap gap-2 align-items-center mt-3">
            <Button variant="outline-primary" size="sm" onClick={saveGroqApiKey} disabled={savingSettings}>
              {savingSettings ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-2" />
                  {t('common.blog.saving', currentLanguage)}
                </>
              ) : t('blogAdmin.saveSiteSettings', currentLanguage)}
            </Button>
            <Button variant="outline-secondary" size="sm" onClick={resetUiDefaults} disabled={savingSettings}>
              {t('blogAdmin.resetSiteDefaults', currentLanguage)}
            </Button>
          </div>
          <div className="mt-3">
            <h6 className="fw-bold mb-2">{t('blogAdmin.themeAccessibilityTitle', currentLanguage)}</h6>
            <Alert variant={contrastAudit.allPass ? 'success' : 'warning'} className="py-2 mb-2">
              {contrastAudit.allPass
                ? t('blogAdmin.themeAccessibilityPass', currentLanguage)
                : t('blogAdmin.themeAccessibilityWarn', currentLanguage)}
            </Alert>
            <div className="d-flex flex-column gap-1">
              {contrastAudit.checks.map((check) => (
                <div key={check.key} className="small d-flex justify-content-between align-items-center">
                  <span>{t(`blogAdmin.themeCheck${check.key}`, currentLanguage)}</span>
                  <Badge bg={check.pass ? 'success' : 'warning'}>{check.ratio.toFixed(2)}:1</Badge>
                </div>
              ))}
            </div>
          </div>
          {user?.role === 'admin' && (
            <>
              <hr />
              <h6 className="fw-bold mb-2">{t('blogAdmin.contactSettingsTitle', currentLanguage)}</h6>
              <p className="text-muted small mb-3">{t('blogAdmin.contactSettingsHelp', currentLanguage)}</p>
              {contactSettingsMessage && (
                <Alert variant={contactSettingsMessage.variant} dismissible onClose={() => setContactSettingsMessage(null)} className="py-2">
                  {contactSettingsMessage.text}
                </Alert>
              )}
              <Row className="g-2">
                <Col md={6}>
                  <Form.Label>{t('blogAdmin.contactWhatsappPyLabel', currentLanguage)}</Form.Label>
                  <Form.Control
                    value={contactSettings.whatsappParaguay}
                    onChange={(e) => setContactSettings((s) => ({ ...s, whatsappParaguay: e.target.value }))}
                    placeholder={t('blogAdmin.contactWhatsappPyPlaceholder', currentLanguage)}
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>{t('blogAdmin.contactWhatsappBoLabel', currentLanguage)}</Form.Label>
                  <Form.Control
                    value={contactSettings.whatsappBolivia}
                    onChange={(e) => setContactSettings((s) => ({ ...s, whatsappBolivia: e.target.value }))}
                    placeholder={t('blogAdmin.contactWhatsappBoPlaceholder', currentLanguage)}
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>{t('blogAdmin.contactEmailLabel', currentLanguage)}</Form.Label>
                  <Form.Control
                    type="email"
                    value={contactSettings.email}
                    onChange={(e) => setContactSettings((s) => ({ ...s, email: e.target.value }))}
                    placeholder={t('blogAdmin.contactEmailPlaceholder', currentLanguage)}
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>{t('blogAdmin.contactSupportEsLabel', currentLanguage)}</Form.Label>
                  <Form.Control
                    value={contactSettings.supportNoteEs}
                    onChange={(e) => setContactSettings((s) => ({ ...s, supportNoteEs: e.target.value }))}
                    placeholder={t('blogAdmin.contactSupportEsPlaceholder', currentLanguage)}
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>{t('blogAdmin.contactSupportEnLabel', currentLanguage)}</Form.Label>
                  <Form.Control
                    value={contactSettings.supportNoteEn}
                    onChange={(e) => setContactSettings((s) => ({ ...s, supportNoteEn: e.target.value }))}
                    placeholder={t('blogAdmin.contactSupportEnPlaceholder', currentLanguage)}
                  />
                </Col>
              </Row>
              <div className="d-flex flex-wrap gap-2 align-items-center mt-3">
                <Button variant="outline-primary" size="sm" onClick={saveContactSettings} disabled={savingContactSettings}>
                  {savingContactSettings ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" className="me-2" />
                      {t('common.blog.saving', currentLanguage)}
                    </>
                  ) : t('blogAdmin.saveContactSettings', currentLanguage)}
                </Button>
              </div>
            </>
          )}
        </Card.Body>
      </Card>
      )}
      {activePanel === 'catalog' && (
      <Card className="mb-4 border-secondary">
        <Card.Body>
          <h5 className="mb-2">{t('blogAdmin.catalogSectionTitle', currentLanguage)}</h5>
          <p className="text-muted small mb-3">{t('blogAdmin.catalogSectionHelp', currentLanguage)}</p>
          {catalogMessage && (
            <Alert variant={catalogMessage.variant} dismissible onClose={() => setCatalogMessage(null)} className="py-2">
              {catalogMessage.text}
            </Alert>
          )}
          <Row className="g-4">
            <Col lg={5}>
              <h6 className="fw-bold mb-2">{t('blogAdmin.categoriesTitle', currentLanguage)}</h6>
              <Form onSubmit={handleCategorySubmit} className="mb-3">
                <Form.Control
                  className="mb-2"
                  placeholder={t('blogAdmin.categorySlugPlaceholder', currentLanguage)}
                  value={categoryForm.slug}
                  onChange={(e) => setCategoryForm((s) => ({ ...s, slug: e.target.value }))}
                  required
                />
                <Form.Control
                  className="mb-2"
                  placeholder={t('blogAdmin.categoryNamePlaceholder', currentLanguage)}
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm((s) => ({ ...s, name: e.target.value }))}
                  required
                />
                <Form.Control
                  className="mb-2"
                  placeholder={t('blogAdmin.categoryDescPlaceholder', currentLanguage)}
                  value={categoryForm.description}
                  onChange={(e) => setCategoryForm((s) => ({ ...s, description: e.target.value }))}
                />
                <div className="d-flex gap-2">
                  <Button size="sm" type="submit">{t('blogAdmin.saveCategory', currentLanguage)}</Button>
                  {categoryForm.id && (
                    <Button size="sm" variant="outline-secondary" type="button" onClick={resetCategoryForm}>
                      {t('blogAdmin.cancelEdit', currentLanguage)}
                    </Button>
                  )}
                </div>
              </Form>
              <div className="small">
                {categories.map((cat) => (
                  <div key={cat.id} className="d-flex justify-content-between align-items-center border rounded px-2 py-1 mb-2">
                    <span><strong>{cat.name}</strong> ({cat.slug})</span>
                    <Button size="sm" variant="outline-primary" onClick={() => editCategory(cat)}>
                      {t('common.edit', currentLanguage)}
                    </Button>
                  </div>
                ))}
              </div>
            </Col>
            <Col lg={7}>
              <h6 className="fw-bold mb-2">{t('blogAdmin.productsTitle', currentLanguage)}</h6>
              <Form onSubmit={handleProductSubmit} className="mb-3">
                <Row className="g-2">
                  <Col md={6}>
                    <Form.Control
                      placeholder={t('blogAdmin.productNamePlaceholder', currentLanguage)}
                      value={productForm.name}
                      onChange={(e) => setProductForm((s) => ({ ...s, name: e.target.value }))}
                      required
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Control
                      placeholder={t('blogAdmin.productSlugPlaceholder', currentLanguage)}
                      value={productForm.slug}
                      onChange={(e) => setProductForm((s) => ({ ...s, slug: e.target.value }))}
                      required
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label className="small">{t('blogAdmin.productPriceLabel', currentLanguage)}</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder={t('blogAdmin.pricePlaceholder', currentLanguage)}
                      value={productForm.price}
                      onChange={(e) => setProductForm((s) => ({ ...s, price: e.target.value }))}
                      required
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label className="small">{t('blogAdmin.productComparePriceLabel', currentLanguage)}</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder={t('blogAdmin.productComparePricePlaceholder', currentLanguage)}
                      value={productForm.compare_at_price}
                      onChange={(e) => setProductForm((s) => ({ ...s, compare_at_price: e.target.value }))}
                    />
                    <Form.Text className="text-muted">{t('blogAdmin.productComparePriceHelp', currentLanguage)}</Form.Text>
                  </Col>
                  <Col md={4}>
                    <Form.Label className="small">{t('blogAdmin.productCurrencyLabel', currentLanguage)}</Form.Label>
                    <Form.Control
                      placeholder="USD"
                      value={productForm.currency}
                      onChange={(e) => setProductForm((s) => ({ ...s, currency: e.target.value }))}
                    />
                  </Col>
                  <Col xs={12} lg={6}>
                    <Form.Select
                      value={productForm.category_id}
                      onChange={(e) => setProductForm((s) => ({ ...s, category_id: e.target.value }))}
                    >
                      <option value="">{t('blogAdmin.noCategoryOption', currentLanguage)}</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={12}>
                    <Form.Control
                      placeholder={t('blogAdmin.productDescPlaceholder', currentLanguage)}
                      value={productForm.description}
                      onChange={(e) => setProductForm((s) => ({ ...s, description: e.target.value }))}
                    />
                  </Col>
                  <Col md={12}>
                    <p className="small fw-semibold mb-2">{t('blogAdmin.productIconLabel', currentLanguage)}</p>
                    <p className="small text-muted mb-2">{t('blogAdmin.productIconHelp', currentLanguage)}</p>
                    <div className="d-flex flex-wrap align-items-end gap-3 mb-2">
                      <div style={{ minWidth: 180 }}>
                        <Form.Label className="small">{t('blogAdmin.productIconPickerLabel', currentLanguage)}</Form.Label>
                        <Form.Select
                          value={productForm.icon_key}
                          onChange={(e) => setProductForm((s) => ({ ...s, icon_key: e.target.value }))}
                        >
                          {PRODUCT_ICON_OPTIONS.map((opt) => (
                            <option key={opt.key} value={opt.key}>{t(opt.labelKey, currentLanguage)}</option>
                          ))}
                        </Form.Select>
                      </div>
                      <div style={{ minWidth: 160 }}>
                        <Form.Label className="small">{t('blogAdmin.productIconColorLabel', currentLanguage)}</Form.Label>
                        <Form.Select
                          value={productForm.icon_color}
                          onChange={(e) => setProductForm((s) => ({ ...s, icon_color: e.target.value }))}
                        >
                          {PRODUCT_ICON_COLOR_OPTIONS.map((opt) => (
                            <option key={opt.key} value={opt.key}>{t(opt.labelKey, currentLanguage)}</option>
                          ))}
                        </Form.Select>
                      </div>
                      <ProductIcon
                        iconKey={productForm.icon_key}
                        iconColor={productForm.icon_color}
                        size="lg"
                      />
                    </div>
                  </Col>
                  <Col md={12}>
                    <Form.Check
                      type="switch"
                      id="product-form-active"
                      label={t('blogAdmin.productFormActiveLabel', currentLanguage)}
                      checked={!!productForm.active}
                      onChange={(e) => setProductForm((s) => ({ ...s, active: e.target.checked }))}
                    />
                  </Col>
                  <Col md={12}>
                    <Form.Check
                      type="switch"
                      id="product-form-direct-checkout"
                      label={t('blogAdmin.productFormDirectCheckoutLabel', currentLanguage)}
                      checked={!!productForm.direct_checkout}
                      onChange={(e) => setProductForm((s) => ({ ...s, direct_checkout: e.target.checked }))}
                    />
                    <Form.Text className="text-muted">{t('blogAdmin.productFormDirectCheckoutHelp', currentLanguage)}</Form.Text>
                  </Col>
                  <Col md={12}>
                    <p className="small fw-semibold mb-1">{t('blogAdmin.productPrimaryImage', currentLanguage)}</p>
                    <p className="small text-muted mb-2">{t('blogAdmin.productPrimaryImageHelp', currentLanguage)}</p>
                    <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={handleProductPrimaryUpload}
                        disabled={uploadingProductImage}
                        className="w-auto"
                      />
                      {productForm.primary_image_url && (
                        <Button
                          size="sm"
                          variant={productForm.og_image_url === productForm.primary_image_url ? 'success' : 'outline-success'}
                          type="button"
                          onClick={() => setProductOgImage(productForm.primary_image_url)}
                        >
                          {productForm.og_image_url === productForm.primary_image_url ? 'OG selected' : 'Use as OG preview'}
                        </Button>
                      )}
                      {productForm.primary_image_url && (
                        <Button
                          size="sm"
                          variant="outline-danger"
                          type="button"
                          onClick={() => setProductForm((s) => ({
                            ...s,
                            primary_image_url: '',
                            og_image_url: s.og_image_url === s.primary_image_url ? '' : s.og_image_url
                          }))}
                        >
                          {t('blogAdmin.removeImage', currentLanguage)}
                        </Button>
                      )}
                    </div>
                    {productForm.primary_image_url && (
                      <img
                        src={productImageUrl(productForm.primary_image_url)}
                        alt=""
                        className="rounded border mb-2"
                        style={{ maxHeight: 120, objectFit: 'cover' }}
                      />
                    )}
                    <div className="small text-muted mb-2">
                      Current OG preview: {productForm.og_image_url ? productForm.og_image_url : 'auto (primary/gallery/fallback)'}
                    </div>
                  </Col>
                  <Col md={12}>
                    <p className="small fw-semibold mb-1">{t('blogAdmin.productGalleryImages', currentLanguage)}</p>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleProductGalleryUpload}
                      disabled={uploadingProductImage}
                      className="mb-2 w-auto"
                    />
                    {(productForm.gallery_images || []).length > 0 && (
                      <div className="d-flex flex-wrap gap-2 mb-2">
                        {(productForm.gallery_images || []).map((url) => (
                          <div key={url} className="position-relative border rounded p-1" style={{ width: 96 }}>
                            <img
                              src={productImageUrl(url)}
                              alt=""
                              className="rounded w-100"
                              style={{ height: 72, objectFit: 'cover' }}
                            />
                            <div className="d-flex flex-column gap-1 mt-1">
                              <Button
                                size="sm"
                                variant={productForm.og_image_url === url ? 'success' : 'outline-success'}
                                type="button"
                                onClick={() => setProductOgImage(url)}
                              >
                                {productForm.og_image_url === url ? 'OG selected' : 'Use as OG preview'}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-primary"
                                type="button"
                                onClick={() => setProductGalleryAsPrimary(url)}
                              >
                                {t('blogAdmin.setAsPrimaryImage', currentLanguage)}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                type="button"
                                onClick={() => removeProductGalleryImage(url)}
                              >
                                {t('blogAdmin.removeImage', currentLanguage)}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="mt-3">
                      <p className="small fw-semibold mb-2">All current product images</p>
                      {productAllImages.length > 0 ? (
                        <div className="d-flex flex-wrap gap-2">
                          {productAllImages.map((item) => (
                            <div key={item.url} className="position-relative border rounded p-1" style={{ width: 120 }}>
                              <img
                                src={productImageUrl(item.url)}
                                alt=""
                                className="rounded w-100"
                                style={{ height: 78, objectFit: 'cover' }}
                              />
                              <div className="d-flex flex-wrap gap-1 mt-1 mb-1">
                                {item.isPrimary && <span className="badge bg-primary">Primary</span>}
                                {item.isOg && <span className="badge bg-success">OG</span>}
                                {item.isGallery && <span className="badge bg-secondary">Gallery</span>}
                              </div>
                              <div className="d-flex flex-column gap-1">
                                {!item.isPrimary && (
                                  <Button
                                    size="sm"
                                    variant="outline-primary"
                                    type="button"
                                    onClick={() => setProductGalleryAsPrimary(item.url)}
                                  >
                                    {t('blogAdmin.setAsPrimaryImage', currentLanguage)}
                                  </Button>
                                )}
                                {!item.isOg && (
                                  <Button
                                    size="sm"
                                    variant="outline-success"
                                    type="button"
                                    onClick={() => setProductOgImage(item.url)}
                                  >
                                    Use as OG preview
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  type="button"
                                  onClick={() => removeProductImageEverywhere(item.url)}
                                >
                                  Remove from product
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="small text-muted">
                          No images saved yet for this product. Upload a primary or gallery image to manage it here.
                        </div>
                      )}
                    </div>
                    {productForm.og_image_url && (
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        type="button"
                        onClick={() => setProductOgImage('')}
                      >
                        Clear OG selection (auto mode)
                      </Button>
                    )}
                  </Col>
                  <Col md={12} className="d-flex gap-2">
                    <Button size="sm" type="submit">{t('blogAdmin.saveProduct', currentLanguage)}</Button>
                    {productForm.id && (
                      <Button size="sm" variant="outline-secondary" type="button" onClick={resetProductForm}>
                        {t('blogAdmin.cancelEdit', currentLanguage)}
                      </Button>
                    )}
                  </Col>
                </Row>
              </Form>
            </Col>
            <Col xs={12}>
              {catalogLoading ? (
                <div className="small text-muted">{t('common.loading', currentLanguage)}</div>
              ) : (
                <Table responsive size="sm" className="mb-0 blog-admin-data-table">
                  <thead>
                    <tr>
                      <th>{t('blogAdmin.productTableName', currentLanguage)}</th>
                      <th>{t('blogAdmin.productTableCategory', currentLanguage)}</th>
                      <th>{t('blogAdmin.productTablePrice', currentLanguage)}</th>
                      <th>{t('blogAdmin.productTableEnabled', currentLanguage)}</th>
                      <th>{t('blogAdmin.productTableDirectCheckout', currentLanguage)}</th>
                      <th className="blog-admin-data-table-actions">{t('common.blog.actions', currentLanguage)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((item) => (
                      <tr key={item.id} className={item.active ? undefined : 'blog-admin-product-row-inactive'}>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <ProductIcon iconKey={item.icon_key} iconColor={item.icon_color} />
                            <span>{item.name}</span>
                          </div>
                        </td>
                        <td>{item.category_name || '-'}</td>
                        <td className="text-nowrap">
                          {(() => {
                            const pricing = getProductSalePricing(item);
                            if (pricing.hasDiscount) {
                              return (
                                <>
                                  <div className="small text-muted text-decoration-line-through">
                                    {item.currency} {formatMoneyAmount(pricing.compareAt)}
                                  </div>
                                  <div>
                                    {item.currency} {formatMoneyAmount(pricing.salePrice)}
                                    <span className="ms-1 small text-warning">-{pricing.discountPercent}%</span>
                                  </div>
                                </>
                              );
                            }
                            return `${item.currency} ${item.price}`;
                          })()}
                        </td>
                        <td>
                          <Form.Check
                            type="switch"
                            id={`product-active-${item.id}`}
                            label={item.active
                              ? t('blogAdmin.productActiveOn', currentLanguage)
                              : t('blogAdmin.productActiveOff', currentLanguage)}
                            checked={!!item.active}
                            disabled={productActiveSavingId === item.id}
                            onChange={(e) => toggleProductActive(item, e.target.checked)}
                          />
                        </td>
                        <td>
                          <Form.Check
                            type="switch"
                            id={`product-direct-checkout-${item.id}`}
                            label={item.direct_checkout
                              ? t('blogAdmin.productDirectCheckoutOn', currentLanguage)
                              : t('blogAdmin.productDirectCheckoutOff', currentLanguage)}
                            checked={!!item.direct_checkout}
                            disabled={productDirectCheckoutSavingId === item.id}
                            onChange={(e) => toggleProductDirectCheckout(item, e.target.checked)}
                          />
                        </td>
                        <td className="blog-admin-data-table-actions">
                          <div className="d-flex flex-wrap gap-1">
                            {getProductUrl(item) && (
                              <Button
                                size="sm"
                                variant="outline-info"
                                onClick={() => window.open(getProductUrl(item), '_blank')}
                                title={t('blogAdmin.viewProduct', currentLanguage)}
                              >
                                <FaEye />
                              </Button>
                            )}
                            <Button size="sm" variant="outline-primary" onClick={() => editProduct(item)}>
                              {t('common.edit', currentLanguage)}
                            </Button>
                            <Button size="sm" variant="outline-danger" onClick={() => deleteProductItem(item.id)}>
                              {t('common.delete', currentLanguage)}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {products.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center text-muted">{t('blogAdmin.noProducts', currentLanguage)}</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>
      )}
      {activePanel === 'sales' && (
      <Card className="mb-4 border-secondary">
        <Card.Body>
          <h5 className="mb-2">{t('blogAdmin.quoteWizardTitle', currentLanguage)}</h5>
          <p className="text-muted small mb-3">{t('blogAdmin.quoteWizardHelp', currentLanguage)}</p>
          {quoteMessage && (
            <Alert variant={quoteMessage.variant} dismissible onClose={() => setQuoteMessage(null)} className="py-2">
              {quoteMessage.text}
            </Alert>
          )}
          <div className="d-flex flex-wrap gap-2 mb-3">
            <Badge bg={quoteStep === 1 ? 'primary' : 'secondary'}>1. {t('blogAdmin.quoteStepScope', currentLanguage)}</Badge>
            <Badge bg={quoteStep === 2 ? 'primary' : 'secondary'}>2. {t('blogAdmin.quoteStepComplexity', currentLanguage)}</Badge>
            <Badge bg={quoteStep === 3 ? 'primary' : 'secondary'}>3. {t('blogAdmin.quoteStepResult', currentLanguage)}</Badge>
          </div>

          {quoteStep === 1 && (
            <Row className="g-2">
              <Col md={4}>
                <Form.Label>{t('blogAdmin.quoteProjectType', currentLanguage)}</Form.Label>
                <Form.Select
                  value={quoteForm.projectType}
                  onChange={(e) => setQuoteForm((s) => ({ ...s, projectType: e.target.value }))}
                >
                  <option value="website">{t('blogAdmin.quoteProjectWebsite', currentLanguage)}</option>
                  <option value="software">{t('blogAdmin.quoteProjectSoftware', currentLanguage)}</option>
                </Form.Select>
              </Col>
              <Col md={4}>
                <Form.Label>{t('blogAdmin.quoteWebsiteType', currentLanguage)}</Form.Label>
                <Form.Select
                  value={quoteForm.websiteType}
                  onChange={(e) => setQuoteForm((s) => ({ ...s, websiteType: e.target.value }))}
                  disabled={quoteForm.projectType !== 'website'}
                >
                  <option value="landing">{t('blogAdmin.quoteWebsiteLanding', currentLanguage)}</option>
                  <option value="corporate">{t('blogAdmin.quoteWebsiteCorporate', currentLanguage)}</option>
                  <option value="ecommerce">{t('blogAdmin.quoteWebsiteEcommerce', currentLanguage)}</option>
                </Form.Select>
              </Col>
              <Col md={4}>
                <Form.Label>{t('blogAdmin.quoteModules', currentLanguage)}</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  max="30"
                  value={quoteForm.modules}
                  onChange={(e) => setQuoteForm((s) => ({ ...s, modules: Number(e.target.value || 1) }))}
                />
              </Col>
            </Row>
          )}

          {quoteStep === 2 && (
            <Row className="g-2">
              <Col md={4}>
                <Form.Label>{t('blogAdmin.quoteIntegrations', currentLanguage)}</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  max="20"
                  value={quoteForm.integrations}
                  onChange={(e) => setQuoteForm((s) => ({ ...s, integrations: Number(e.target.value || 0) }))}
                />
              </Col>
              <Col md={4}>
                <Form.Label>{t('blogAdmin.quoteComplexity', currentLanguage)}</Form.Label>
                <Form.Select
                  value={quoteForm.complexity}
                  onChange={(e) => setQuoteForm((s) => ({ ...s, complexity: e.target.value }))}
                >
                  <option value="low">{t('blogAdmin.quoteComplexityLow', currentLanguage)}</option>
                  <option value="medium">{t('blogAdmin.quoteComplexityMedium', currentLanguage)}</option>
                  <option value="high">{t('blogAdmin.quoteComplexityHigh', currentLanguage)}</option>
                </Form.Select>
              </Col>
              <Col md={4}>
                <Form.Label>{t('blogAdmin.quoteTimeline', currentLanguage)}</Form.Label>
                <Form.Select
                  value={quoteForm.timeline}
                  onChange={(e) => setQuoteForm((s) => ({ ...s, timeline: e.target.value }))}
                >
                  <option value="standard">{t('blogAdmin.quoteTimelineStandard', currentLanguage)}</option>
                  <option value="fast">{t('blogAdmin.quoteTimelineFast', currentLanguage)}</option>
                  <option value="urgent">{t('blogAdmin.quoteTimelineUrgent', currentLanguage)}</option>
                </Form.Select>
              </Col>
              <Col md={4}>
                <Form.Label>{t('blogAdmin.quoteSupportMonths', currentLanguage)}</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  max="24"
                  value={quoteForm.supportMonths}
                  onChange={(e) => setQuoteForm((s) => ({ ...s, supportMonths: Number(e.target.value || 0) }))}
                />
              </Col>
              <Col md={8}>
                <div className="d-flex flex-wrap gap-3 mt-4">
                  <Form.Check
                    type="switch"
                    id="quote-automation"
                    label={t('blogAdmin.quoteAutomation', currentLanguage)}
                    checked={quoteForm.automation}
                    onChange={(e) => setQuoteForm((s) => ({ ...s, automation: e.target.checked }))}
                  />
                  <Form.Check
                    type="switch"
                    id="quote-seo"
                    label={t('blogAdmin.quoteSeo', currentLanguage)}
                    checked={quoteForm.seo}
                    onChange={(e) => setQuoteForm((s) => ({ ...s, seo: e.target.checked }))}
                  />
                  <Form.Check
                    type="switch"
                    id="quote-ux"
                    label={t('blogAdmin.quoteUxResearch', currentLanguage)}
                    checked={quoteForm.uxResearch}
                    onChange={(e) => setQuoteForm((s) => ({ ...s, uxResearch: e.target.checked }))}
                  />
                </div>
              </Col>
            </Row>
          )}

          {quoteStep === 3 && quoteResult && (
            <div>
              <Alert variant="success" className="mb-2">
                {t('blogAdmin.quoteEstimatedRange', currentLanguage)}: <strong>{quoteForm.currency} {quoteResult.low} - {quoteResult.high}</strong>
              </Alert>
              <p className="small text-muted mb-2">
                {t('blogAdmin.quoteComplexityScore', currentLanguage)}: <strong>{quoteResult.score}</strong>
              </p>
              <ul className="small text-muted mb-0">
                {quoteResult.assumptions.map((key) => (
                  <li key={key}>{t(`blogAdmin.${key}`, currentLanguage)}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="d-flex gap-2 mt-3">
            <Button variant="outline-secondary" size="sm" onClick={prevQuoteStep} disabled={quoteStep === 1}>
              {t('blogAdmin.quoteBack', currentLanguage)}
            </Button>
            {quoteStep < 2 && (
              <Button variant="primary" size="sm" onClick={nextQuoteStep}>
                {t('blogAdmin.quoteNext', currentLanguage)}
              </Button>
            )}
            {quoteStep === 2 && (
              <Button variant="primary" size="sm" onClick={runQuote}>
                {t('blogAdmin.quoteCalculate', currentLanguage)}
              </Button>
            )}
            {quoteStep === 3 && (
              <Button variant="success" size="sm" onClick={saveCurrentQuote} disabled={quoteSaving}>
                {quoteSaving ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    {t('common.blog.saving', currentLanguage)}
                  </>
                ) : t('blogAdmin.quoteSave', currentLanguage)}
              </Button>
            )}
            {quoteStep === 3 && (
              <Button variant="outline-primary" size="sm" onClick={() => { setQuoteResult(null); setQuoteStep(1); }}>
                {t('blogAdmin.quoteRestart', currentLanguage)}
              </Button>
            )}
          </div>
          <hr />
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="fw-bold mb-0">{t('blogAdmin.quotePipelineTitle', currentLanguage)}</h6>
            <div className="d-flex gap-2">
              <Button variant="outline-secondary" size="sm" onClick={loadSalesQuotes} disabled={salesLoading || salesOpsLoading}>
                {salesLoading ? t('blogAdmin.quotePipelineLoading', currentLanguage) : t('blogAdmin.quotePipelineRefresh', currentLanguage)}
              </Button>
              <Button variant="outline-primary" size="sm" onClick={exportQuotesCsv} disabled={salesOpsLoading}>
                {t('blogAdmin.quoteExportCsv', currentLanguage)}
              </Button>
              <Button variant="outline-primary" size="sm" onClick={exportQuotesCrm} disabled={salesOpsLoading}>
                {t('blogAdmin.quoteExportCrm', currentLanguage)}
              </Button>
              <Button variant="outline-warning" size="sm" onClick={runQuoteReminders} disabled={salesOpsLoading}>
                {t('blogAdmin.quoteRunReminders', currentLanguage)}
              </Button>
            </div>
          </div>
          <Row className="g-2 mb-2">
            <Col md={3}>
              <Form.Select
                size="sm"
                value={salesFilters.stage}
                onChange={(e) => setSalesFilters((s) => ({ ...s, stage: e.target.value }))}
              >
                <option value="">{t('blogAdmin.quoteFilterAllStages', currentLanguage)}</option>
                <option value="new">{t('blogAdmin.quoteStageNew', currentLanguage)}</option>
                <option value="qualified">{t('blogAdmin.quoteStageQualified', currentLanguage)}</option>
                <option value="proposal">{t('blogAdmin.quoteStageProposal', currentLanguage)}</option>
                <option value="won">{t('blogAdmin.quoteStageWon', currentLanguage)}</option>
                <option value="lost">{t('blogAdmin.quoteStageLost', currentLanguage)}</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select
                size="sm"
                value={salesFilters.projectType}
                onChange={(e) => setSalesFilters((s) => ({ ...s, projectType: e.target.value }))}
              >
                <option value="">{t('blogAdmin.quoteFilterAllProjects', currentLanguage)}</option>
                <option value="website">{t('blogAdmin.quoteProjectWebsite', currentLanguage)}</option>
                <option value="software">{t('blogAdmin.quoteProjectSoftware', currentLanguage)}</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select
                size="sm"
                value={salesFilters.assignedTo}
                onChange={(e) => setSalesFilters((s) => ({ ...s, assignedTo: e.target.value }))}
              >
                <option value="">{t('blogAdmin.quoteFilterAllOwners', currentLanguage)}</option>
                {(staffUsers || []).map((u) => (
                  <option key={u.id} value={u.id}>{u.name || u.email || `#${u.id}`}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Control
                size="sm"
                placeholder={t('blogAdmin.quoteFilterSearch', currentLanguage)}
                value={salesFilters.search}
                onChange={(e) => setSalesFilters((s) => ({ ...s, search: e.target.value }))}
              />
            </Col>
          </Row>
          <Table responsive size="sm" bordered hover className="mb-0">
            <thead>
              <tr>
                <th>{t('blogAdmin.quoteTableId', currentLanguage)}</th>
                <th>{t('blogAdmin.quoteTableProject', currentLanguage)}</th>
                <th>{t('blogAdmin.quoteTableRange', currentLanguage)}</th>
                <th>{t('blogAdmin.quoteTableStage', currentLanguage)}</th>
                <th>{t('blogAdmin.quoteTableOwner', currentLanguage)}</th>
                <th>{t('blogAdmin.quoteTableNotes', currentLanguage)}</th>
              </tr>
            </thead>
            <tbody>
              {(salesQuotes || []).map((quote) => (
                <tr key={quote.id}>
                  <td>#{quote.id}</td>
                  <td>{quote.project_type === 'software' ? t('blogAdmin.quoteProjectSoftware', currentLanguage) : t('blogAdmin.quoteProjectWebsite', currentLanguage)}</td>
                  <td>{quote.currency} {quote.estimate_low} - {quote.estimate_high}</td>
                  <td style={{ minWidth: 160 }}>
                    <Form.Select
                      size="sm"
                      value={quote.stage || 'new'}
                      onChange={(e) => updateQuoteStage(quote.id, e.target.value)}
                    >
                      <option value="new">{t('blogAdmin.quoteStageNew', currentLanguage)}</option>
                      <option value="qualified">{t('blogAdmin.quoteStageQualified', currentLanguage)}</option>
                      <option value="proposal">{t('blogAdmin.quoteStageProposal', currentLanguage)}</option>
                      <option value="won">{t('blogAdmin.quoteStageWon', currentLanguage)}</option>
                      <option value="lost">{t('blogAdmin.quoteStageLost', currentLanguage)}</option>
                    </Form.Select>
                  </td>
                  <td style={{ minWidth: 200 }}>
                    <Form.Select
                      size="sm"
                      value={quote.assigned_to || ''}
                      onChange={(e) => updateQuoteDetails(quote.id, { assigned_to: e.target.value ? Number(e.target.value) : null })}
                    >
                      <option value="">{t('blogAdmin.quoteUnassigned', currentLanguage)}</option>
                      {(staffUsers || []).map((u) => (
                        <option key={u.id} value={u.id}>{u.name || u.email || `#${u.id}`}</option>
                      ))}
                    </Form.Select>
                  </td>
                  <td style={{ minWidth: 260 }}>
                    <InputGroup size="sm">
                      <Form.Control
                        defaultValue={quote.notes || ''}
                        placeholder={t('blogAdmin.quoteNotesPlaceholder', currentLanguage)}
                        onBlur={(e) => {
                          const next = e.target.value || '';
                          if ((quote.notes || '') !== next) {
                            updateQuoteDetails(quote.id, { notes: next });
                          }
                        }}
                      />
                    </InputGroup>
                  </td>
                </tr>
              ))}
              {!salesLoading && salesQuotes.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-muted">{t('blogAdmin.quotePipelineEmpty', currentLanguage)}</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
      )}
      {activePanel === 'posts' && (
      <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>{t('common.blog.adminTitle', currentLanguage)}</h1>
        <div className="d-flex gap-2">
          <Button variant="outline-primary" onClick={() => { setGenerateTopic(''); setGenerateLanguage(currentLanguage === 'en' ? 'en' : 'es'); setShowGenerateModal(true); }} disabled={generating}>
            <FaMagic className="me-2" />
            {t('common.blog.generateWithAI', currentLanguage)}
          </Button>
          <Button variant="primary" onClick={handleNewPost}>
            <FaPlus className="me-2" />
            {t('common.blog.newPost', currentLanguage)}
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {postsMessage && (
        <Alert variant={postsMessage.variant} dismissible onClose={() => setPostsMessage(null)}>
          {postsMessage.text}
        </Alert>
      )}

      <Card className="mb-3">
        <Card.Body>
          <Row className="g-2 mb-3">
            <Col md={4}>
              <InputGroup>
                <Form.Control
                  type="search"
                  placeholder={t('common.blog.searchPlaceholder', currentLanguage)}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), applySearch())}
                />
                <Button variant="outline-secondary" onClick={applySearch}>
                  {t('common.blog.searchButton', currentLanguage)}
                </Button>
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                aria-label={t('common.blog.filterStatus', currentLanguage)}
              >
                <option value="">{t('common.blog.filterStatusAll', currentLanguage)}</option>
                <option value="true">{t('common.blog.filterStatusPublished', currentLanguage)}</option>
                <option value="false">{t('common.blog.filterStatusDraft', currentLanguage)}</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select
                value={categoryFilter}
                onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
                aria-label={t('common.blog.filterCategory', currentLanguage)}
              >
                <option value="">{t('common.blog.filterCategoryAll', currentLanguage)}</option>
                {BLOG_CATEGORY_OPTIONS.map((categoryId) => (
                  <option key={categoryId} value={categoryId}>
                    {getCategoryLabel(categoryId)}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <Card>
        <Card.Body>
          <Table responsive hover>
            <thead>
              <tr>
                <th>{t('common.blog.title', currentLanguage)}</th>
                <th>{t('common.blog.category', currentLanguage)}</th>
                <th>{t('common.blog.status', currentLanguage)}</th>
                <th>{t('common.blog.publishedAt', currentLanguage)}</th>
                <th>{t('common.blog.views', currentLanguage)}</th>
                <th>{t('common.blog.actions', currentLanguage)}</th>
              </tr>
            </thead>
            <tbody>
              {posts.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center text-muted py-4">
                    {t('common.blog.noPosts', currentLanguage)}
                  </td>
                </tr>
              ) : (
                posts.map((post) => (
                  <tr key={post.id}>
                    <td>
                      <strong>{post.title}</strong>
                      {post.slug && (
                        <div className="small text-muted">/{post.slug}</div>
                      )}
                    </td>
                    <td>
                      <Badge bg={getCategoryColor(post.category)}>
                        {getCategoryLabel(post.category)}
                      </Badge>
                    </td>
                    <td>
                      <Badge bg={post.published ? 'success' : 'secondary'}>
                        {post.published ? t('common.blog.published', currentLanguage) : t('common.blog.draft', currentLanguage)}
                      </Badge>
                    </td>
                    <td>{formatDate(post.published_at)}</td>
                    <td>{post.views || 0}</td>
                    <td>
                      <div className="d-flex gap-2">
                        {getPostUrl(post) && (
                          <Button
                            variant="outline-info"
                            size="sm"
                            onClick={() => window.open(getPostUrl(post), '_blank')}
                            title={t('common.blog.viewPost', currentLanguage)}
                          >
                            <FaEye />
                          </Button>
                        )}
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleEditPost(post)}
                          title={t('common.blog.edit', currentLanguage)}
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeletePost(post.id)}
                          title={t('common.blog.delete', currentLanguage)}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
          {totalPosts > 0 && (
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3 pt-2 border-top">
              <span className="small text-muted">
                {tf('common.blog.showingCount', {
                  from: String((page - 1) * PAGE_SIZE + 1),
                  to: String(Math.min(page * PAGE_SIZE, totalPosts)),
                  total: String(totalPosts)
                }, currentLanguage)}
              </span>
              <Pagination className="mb-0">
                <Pagination.Prev
                  disabled={page <= 1}
                  onClick={(e) => { e?.preventDefault?.(); setPage((p) => Math.max(1, p - 1)); }}
                >
                  {t('common.blog.prev', currentLanguage)}
                </Pagination.Prev>
                <Pagination.Item disabled>
                  {t('common.blog.page', currentLanguage)} {page} / {Math.max(1, Math.ceil(totalPosts / PAGE_SIZE))}
                </Pagination.Item>
                <Pagination.Next
                  disabled={page >= Math.ceil(totalPosts / PAGE_SIZE)}
                  onClick={(e) => { e?.preventDefault?.(); setPage((p) => p + 1); }}
                >
                  {t('common.blog.next', currentLanguage)}
                </Pagination.Next>
              </Pagination>
            </div>
          )}
        </Card.Body>
      </Card>
      </>
      )}

      {/* Modal Generar con IA */}
      <Modal show={showGenerateModal} onHide={() => !generating && setShowGenerateModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaMagic className="me-2" />
            {t('common.blog.generateWithAI', currentLanguage)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>{t('common.blog.generateTopicLabel', currentLanguage)}</Form.Label>
            <Form.Control
              type="text"
              value={generateTopic}
              onChange={(e) => setGenerateTopic(e.target.value)}
              placeholder={t('common.blog.generateTopicPlaceholder', currentLanguage)}
              disabled={generating}
            />
            <Form.Text className="text-muted">
              {t('common.blog.generateTopicHelp', currentLanguage)}
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('common.blog.generateLanguageLabel', currentLanguage)}</Form.Label>
            <Form.Select
              value={generateLanguage}
              onChange={(e) => setGenerateLanguage(e.target.value)}
              disabled={generating}
            >
              <option value="es">{t('common.blog.generateLanguageEs', currentLanguage)}</option>
              <option value="en">{t('common.blog.generateLanguageEn', currentLanguage)}</option>
            </Form.Select>
            <Form.Text className="text-muted">
              {t('common.blog.generateLanguageHelp', currentLanguage)}
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowGenerateModal(false)} disabled={generating}>
            {t('common.close', currentLanguage)}
          </Button>
          <Button variant="primary" onClick={handleGenerateWithAI} disabled={generating || !generateTopic.trim()}>
            {generating ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {t('common.blog.generating', currentLanguage)}
              </>
            ) : (
              <>
                <FaMagic className="me-2" />
                {t('common.blog.generateWithAI', currentLanguage)}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de edición/creación */}
      <Modal show={showModal} onHide={() => { setShowModal(false); setLastUsage(null); setLastPromptSent(null); setShowPromptSent(false); }} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingPost ? t('common.blog.editPost', currentLanguage) : t('common.blog.newPost', currentLanguage)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {lastUsage && (
            <Alert variant="info" className="mb-3 small">
              <strong>{t('common.blog.usageTitle', currentLanguage)}</strong>
              <ul className="mb-0 mt-1">
                {lastUsage.prompt_tokens != null && (
                  <li>{t('common.blog.usagePromptTokens', currentLanguage)}: {lastUsage.prompt_tokens}</li>
                )}
                {lastUsage.completion_tokens != null && (
                  <li>{t('common.blog.usageCompletionTokens', currentLanguage)}: {lastUsage.completion_tokens}</li>
                )}
                {lastUsage.total_tokens != null && (
                  <li>{t('common.blog.usageTotalTokens', currentLanguage)}: {lastUsage.total_tokens}</li>
                )}
                {lastUsage.model && (
                  <li>{t('common.blog.usageModel', currentLanguage)}: {lastUsage.model}</li>
                )}
                {lastUsage.response_time_ms != null && (
                  <li>{t('common.blog.usageResponseTime', currentLanguage)}: {(lastUsage.response_time_ms / 1000).toFixed(2)} s</li>
                )}
              </ul>
              <p className="mb-0 mt-1 small text-muted">{t('common.blog.generateSuccess', currentLanguage)}</p>
            </Alert>
          )}
          {lastPromptSent && (
            <div className="mb-3">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowPromptSent((v) => !v)}
                aria-expanded={showPromptSent}
              >
                {t('common.blog.promptSentTitle', currentLanguage)}
              </Button>
              <Collapse in={showPromptSent}>
                <div className="mt-2">
                  {lastPromptSent.system && (
                    <div className="mb-2">
                      <strong className="small text-muted">{t('common.blog.promptSentSystem', currentLanguage)}</strong>
                      <pre className="bg-light p-2 rounded small mb-0" style={{ maxHeight: '200px', overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {lastPromptSent.system}
                      </pre>
                    </div>
                  )}
                  {lastPromptSent.user && (
                    <div>
                      <strong className="small text-muted">{t('common.blog.promptSentUser', currentLanguage)}</strong>
                      <pre className="bg-light p-2 rounded small mb-0" style={{ maxHeight: '200px', overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {lastPromptSent.user}
                      </pre>
                    </div>
                  )}
                </div>
              </Collapse>
            </div>
          )}
          <Form>
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('common.blog.title', currentLanguage)} *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder={t('common.blog.titlePlaceholder', currentLanguage)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('common.blog.category', currentLanguage)} *</Form.Label>
                  <Form.Select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    <option value="business">{getCategoryLabel('business')}</option>
                    <option value="tech">{getCategoryLabel('tech')}</option>
                    <option value="news">{getCategoryLabel('news')}</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('common.blog.postLanguage', currentLanguage)}</Form.Label>
                  <Form.Select
                    value={formData.language}
                    onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                  >
                    <option value="es">{t('common.blog.generateLanguageEs', currentLanguage)} → /noticias/actualidad/...</option>
                    <option value="en">{t('common.blog.generateLanguageEn', currentLanguage)} → /news/news/...</option>
                  </Form.Select>
                  <Form.Text className="text-muted">{t('common.blog.postLanguageHelp', currentLanguage)}</Form.Text>
                </Form.Group>
              </Col>
              <Col md={5}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('common.blog.relatedTitleLabel', currentLanguage)}</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.related_title || ''}
                    onChange={(e) => setFormData({ ...formData, related_title: e.target.value })}
                    placeholder={t('common.blog.relatedTitlePlaceholder', currentLanguage)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('common.blog.relatedYearLabel', currentLanguage)}</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.related_year || ''}
                    onChange={(e) => setFormData({ ...formData, related_year: e.target.value })}
                    placeholder={t('common.blog.relatedYearPlaceholder', currentLanguage)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>{t('common.blog.excerpt', currentLanguage)}</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                placeholder={t('common.blog.excerptPlaceholder', currentLanguage)}
              />
              <Form.Text className="text-muted">
                {t('common.blog.excerptHelp', currentLanguage)}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('common.blog.content', currentLanguage)} *</Form.Label>
              <div className="d-flex flex-wrap gap-2 mb-2 align-items-center">
                <span className="small text-muted">{t('blogAdmin.templateActionsTitle', currentLanguage)}:</span>
                <Button size="sm" variant="outline-secondary" onClick={() => applyCommercialTemplate('business')}>
                  {t('blogAdmin.templateApplyCaseStudy', currentLanguage)}
                </Button>
                <Button size="sm" variant="outline-secondary" onClick={() => applyCommercialTemplate('tech')}>
                  {t('blogAdmin.templateApplyStrategy', currentLanguage)}
                </Button>
                <Button size="sm" variant="outline-secondary" onClick={() => applyCommercialTemplate('news')}>
                  {t('blogAdmin.templateApplyTactical', currentLanguage)}
                </Button>
              </div>
              <div className="blog-editor-wrapper">
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={formData.content}
                  onChange={(value) => setFormData((prev) => ({ ...prev, content: value }))}
                  modules={quillModules}
                  formats={QUILL_FORMATS}
                  placeholder={t('common.blog.contentPlaceholder', currentLanguage)}
                  style={{ minHeight: 280 }}
                />
                {uploadingEditorImage && (
                  <div className="small text-muted mt-1">{t('common.blog.uploadingImage', currentLanguage)}</div>
                )}
              </div>
              <Form.Text className="text-muted">
                {t('common.blog.contentHelp', currentLanguage)}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('common.blog.featuredImage', currentLanguage)}</Form.Label>
              <div className="d-flex gap-2 flex-wrap align-items-center">
                <Form.Control
                  type="url"
                  className="flex-grow-1"
                  style={{ maxWidth: 400 }}
                  value={formData.featured_image}
                  onChange={(e) => setFormData((prev) => ({ ...prev, featured_image: e.target.value }))}
                  placeholder={t('common.blog.imageUrlPlaceholder', currentLanguage)}
                />
                <input
                  ref={featuredFileInputRef}
                  type="file"
                  accept="image/*"
                  className="d-none"
                  onChange={handleFeaturedImageUpload}
                />
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => featuredFileInputRef.current?.click()}
                  disabled={uploadingFeatured}
                >
                  {uploadingFeatured ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-1" />
                      {t('common.blog.uploadingImage', currentLanguage)}
                    </>
                  ) : (
                    <>
                      <FaUpload className="me-1" />
                      {t('common.blog.uploadImage', currentLanguage)}
                    </>
                  )}
                </Button>
              </div>
              <Form.Text className="text-muted">
                {t('common.blog.uploadImageHelp', currentLanguage)}
              </Form.Text>
              {(formData.related_title || formData.related_year) && (
                <div className="mt-2">
                  <a
                    href={`https://www.google.com/search?tbm=isch&tbs=isz:l&q=${encodeURIComponent(
                      [formData.related_title, formData.related_year].filter(Boolean).join(' ')
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-secondary"
                  >
                    <FaExternalLinkAlt className="me-1" />
                    {t('common.blog.searchImagesOnGoogle', currentLanguage)}
                  </a>
                </div>
              )}
              {formData.featured_image && (
                <div className="mt-2">
                  <img
                    src={featuredImageUrl(formData.featured_image)}
                    alt="Preview"
                    style={{ maxHeight: 120, borderRadius: 8, border: '1px solid #dee2e6' }}
                  />
                </div>
              )}
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label={t('common.blog.published', currentLanguage)}
                    checked={formData.published}
                    onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                  />
                </Form.Group>
              </Col>
              {formData.published && (
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('common.blog.publishedAt', currentLanguage)}</Form.Label>
                    <Form.Control
                      type="date"
                      value={formData.published_at}
                      onChange={(e) => setFormData({ ...formData, published_at: e.target.value })}
                    />
                  </Form.Group>
                </Col>
              )}
            </Row>

            <hr />

            <h5 className="mb-3">{t('common.blog.seoSettings', currentLanguage)}</h5>

            <Form.Group className="mb-3">
              <Form.Label>{t('common.blog.metaTitle', currentLanguage)}</Form.Label>
              <Form.Control
                type="text"
                value={formData.meta_title}
                onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                placeholder={t('common.blog.metaTitlePlaceholder', currentLanguage)}
              />
              <Form.Text className="text-muted">
                {t('common.blog.metaTitleHelp', currentLanguage)}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('common.blog.metaDescription', currentLanguage)}</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.meta_description}
                onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                placeholder={t('common.blog.metaDescriptionPlaceholder', currentLanguage)}
              />
              <Form.Text className="text-muted">
                {t('common.blog.metaDescriptionHelp', currentLanguage)}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('common.blog.metaKeywords', currentLanguage)}</Form.Label>
              <Form.Control
                type="text"
                value={formData.meta_keywords}
                onChange={(e) => setFormData({ ...formData, meta_keywords: e.target.value })}
                placeholder={t('common.blog.metaKeywordsPlaceholder', currentLanguage)}
              />
              <Form.Text className="text-muted">
                {t('common.blog.metaKeywordsHelp', currentLanguage)}
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)} disabled={saving}>
            <FaTimes className="me-2" />
            {t('common.cancel', currentLanguage)}
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving || !formData.title || !formData.content}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {t('common.blog.saving', currentLanguage)}
              </>
            ) : (
              <>
                <FaSave className="me-2" />
                {t('common.blog.save', currentLanguage)}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      </Container>
    </div>
  );
};

export default BlogAdmin;
